import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export type Language = "no" | "en";

export type TranslationKey = keyof typeof translations.no;

const translations = {
  no: {
    // Sidebar
    "nav.dashboard": "Dashboard",
    "nav.courses": "Mine kurs",
    "nav.manageCourses": "Administrer kurs",
    "nav.certificates": "Sertifikater",
    "nav.users": "Brukere",
    "nav.onboarding": "Onboarding",
    "nav.analytics": "Innsikt",
    "nav.progress": "Fremgang",
    "nav.orgStructure": "Organisasjonsstruktur",
    "nav.settings": "Innstillinger",
    "nav.profile": "Profil",
    "nav.signOut": "Logg ut",
    "nav.viewAs": "Vis som",
    "nav.faq": "FAQ",
    "nav.campaigns": "MiniKurs",
    "nav.lightTheme": "Lyst",
    "nav.darkTheme": "Mørkt",
    "nav.hideMenu": "Skjul meny",
    "nav.switchLang": "Bytt til engelsk",
    "nav.learningPaths": "Læringsstier",
    "nav.auditLog": "Aktivitetslogg",

    // Greetings
    "greeting.goodNight": "God natt",
    "greeting.goodMorning": "God morgen",
    "greeting.goodAfternoon": "God ettermiddag",
    "greeting.goodEvening": "God kveld",

    // Dashboard
    "dashboard.welcome": "Velkommen!",
    "dashboard.readyToStart": "Klar for å starte?",
    "dashboard.firstCourseWaiting": "Ditt første kurs venter. Klikk under for å begynne.",
    "dashboard.startFirstCourse": "Start første kurs",
    "dashboard.status": "Status",
    "dashboard.lessons": "Elementer",
    "dashboard.ofCompleted": "av {total} fullført",
    "dashboard.coursesCompleted": "Kurs fullført",
    "dashboard.ofCourses": "av {total} kurs",
    "dashboard.totalProgress": "Total fremgang",
    "dashboard.percentCompleted": "prosent fullført",
    "dashboard.yourCourses": "Dine kurs",
    "dashboard.completedCourses": "Fullførte kurs",
    "dashboard.completed": "Fullført",
    "dashboard.resume": "Fortsett",

    // Badges
    "badges.title": "Merker",
    "badges.points": "poeng",
    "badges.earned": "Opptjent ✓",
    "badges.ofBadgesEarned": "{earned} av {total} merker opptjent",

    // Leaderboard
    "leaderboard.title": "Toppliste",
    "leaderboard.youAre": "Du er #{rank}",
    "leaderboard.you": "(deg)",
    "leaderboard.lessons": "elementer",
    "leaderboard.quizzes": "quizer",
    "leaderboard.courses": "kurs",
    "leaderboard.anonymous": "Anonym",

    // Onboarding
    "onboarding.complete": "Onboarding fullført! 🎉",
    "onboarding.gettingStarted": "Kom i gang",
    "onboarding.tasksCompleted": "{done} av {total} oppgaver fullført",
    "onboarding.allSet": "✨ Du er klar! Velkommen til teamet.",

    // Campaigns
    "campaigns.title": "MiniKurs",
    "campaigns.mandatory": "Obligatorisk",
    "campaigns.overdue": "Forfalt",
    "campaigns.dueIn": "Frist {time}",
    "campaigns.progress": "Fremgang",
    "campaigns.completed": "Fullført",
    "campaigns.completedReview": "Fullført – Se igjen",
    "campaigns.continue": "Fortsett",
    "campaigns.start": "Start",
    "campaigns.new": "NY",
    "campaigns.noContent": "Ingen innhold tilknyttet ennå",
    "campaigns.active": "Aktiv",
    "campaigns.archived": "Arkivert",

    // Top header
    "topHeader.profile": "Profil",
    "topHeader.settings": "Innstillinger",
    "topHeader.signOut": "Logg ut",

    // FAQ
    "faq.title": "Ofte stilte spørsmål",
    "faq.description": "Finn svar på vanlige spørsmål om Trainly.",
    "faq.q1": "Hva er Trainly?",
    "faq.a1": "Trainly er et enkelt og moderne LMS som gjør det lett å planlegge, gjennomføre og følge opp opplæring av ansatte. Alt samles i én oversiktlig plattform.",
    "faq.q2": "Hva skiller Trainly fra andre LMS-systemer?",
    "faq.a2": "Mange LMS-systemer er tunge og kompliserte. Trainly er laget for å være enkelt, raskt å komme i gang med og lett å bruke – både for ledelse og ansatte.",
    "faq.q3": "Hvor raskt kan vi komme i gang?",
    "faq.a3": "De fleste kunder er i gang i løpet av noen dager. Vi hjelper dere med oppsett, struktur og kurs – slik at dere slipper lange prosjekter.",
    "faq.q4": "Kan vi tildele opplæring basert på rolle og lokasjon?",
    "faq.a4": "Ja. Dere kan automatisk tildele kurs basert på rolle, avdeling eller filial. Riktig person får riktig opplæring – uten manuelt arbeid.",
    "faq.q5": "Hvordan følger vi opp ansatte som ikke har gjennomført?",
    "faq.a5": "Systemet sender automatiske påminnelser og gir dere oversikt over hvem som trenger oppfølging.",
    "faq.q6": "Fungerer Trainly på mobil?",
    "faq.a6": "Ja. Ansatte kan gjennomføre opplæring på mobil, nettbrett og PC.",
    "faq.q7": "Kan vi få en demo før vi bestemmer oss?",
    "faq.a7": "Ja. Book en uforpliktende demo, så viser vi dere hvordan Trainly kan gi bedre kontroll og tryggere ansatte.",

    // Gamification categories
    "gamification.general": "Generelt",
    "gamification.lessons": "Leksjoner",
    "gamification.quizzes": "Quizer",
    "gamification.coursesPoints": "Kurs og poeng",

    // Language
    "language.no": "Norsk",
    "language.en": "English",

    // Course Viewer
    "course.notFound": "Kurs ikke funnet",
    "course.goBack": "Gå tilbake",
    "course.locked": "Kurset er låst",
    "course.completeFirst": "Du må fullføre følgende kurs først:",
    "course.backToCourses": "Tilbake til mine kurs",
    "course.element": "element",
    "course.elements": "elementer",
    "course.percentDone": "{percent}% fullført",
    "course.continueCourse": "Fortsett kurset",
    "course.startCourse": "Start kurset",
    "course.courseContent": "Kursinnhold",
    "course.stepOf": "Steg {current} av {total}",
    "course.step": "Steg {current}",
    "course.noLessons": "Ingen elementer i dette kurset ennå.",
    "course.congrats": "Gratulerer! 🎉",
    "course.completedAll": "Du har fullført alle {count} elementer i",
    "course.reviewCourse": "Se gjennom kurset",
    "course.nextCourseReady": "Neste kurs klart for deg:",
    "course.startNextCourse": "Start neste kurs",
    "course.documents": "Dokumenter",
    "course.download": "Last ned",
    "course.completedMark": "Fullført!",
    "course.passQuiz": "Bestå quizen for å fortsette",
    "course.next": "Neste",
    "course.completeCourse": "Fullfør kurset",
    "course.previous": "Forrige",

    // Settings
    "settings.title": "Innstillinger",
    "settings.description": "Administrer organisasjonsinnstillinger og preferanser",
    "settings.companyInfo": "Firmainformasjon",
    "settings.companyInfoDesc": "Rediger grunnleggende informasjon om din organisasjon",
    "settings.companyName": "Firmanavn",
    "settings.directLink": "Direkte lenke",
    "settings.directLinkHelp": "Organisasjonens URL-sti. Subdomener krever egen DNS-konfigurasjon.",
    "settings.welcomeMessage": "Velkomstmelding til ansatte",
    "settings.welcomePlaceholder": "f.eks. Velkommen til opplæringsportalen vår! Her finner du alle kurs.",
    "settings.welcomeHelp": "Vises på dashbordet til alle ansatte",
    "settings.orgId": "Organisasjons-ID",
    "settings.saveChanges": "Lagre endringer",
    "settings.saving": "Lagrer...",
    "settings.yourRole": "Din rolle",
    "settings.yourRoleDesc": "Din tilgang i denne organisasjonen",
    "settings.organization": "Organisasjon",
    "settings.role": "Din rolle",
    "settings.branding": "Merkevarebygging",
    "settings.brandingDesc": "Tilpass organisasjonens logo og temafarger",
    "settings.logo": "Logo",
    "settings.uploadLogo": "Last opp logo",
    "settings.uploading": "Laster opp...",
    "settings.removeLogo": "Fjern",
    "settings.logoRecommend": "Anbefalt: 256x256px, maks 5MB",
    "settings.themeColors": "Temafarger",
    "settings.primaryColor": "Primærfarge",
    "settings.secondaryColor": "Sekundærfarge",
    "settings.saveColors": "Lagre farger",
    "settings.savingColors": "Lagrer...",
    "settings.devMode": "Utviklermodus",
    "settings.devModeDesc": "Bytt visningsrolle for å teste ulike brukeropplevelser",
    "settings.devModeNote": "💡 **Merk:** Dette endrer din UI-visning for testing og påvirker sidepanelnavigasjon umiddelbart. Databasetillatelser forblir basert på din faktiske rolle.",
    "settings.roleChanged": "Rolle endret",
    "settings.viewingAs": "Du ser nå appen som en {role}.",
    "settings.success": "Suksess",
    "settings.error": "Feil",
    "settings.logoUploaded": "Logo beskåret og lastet opp!",
    "settings.logoRemoved": "Logo fjernet.",
    "settings.colorsUpdated": "Temafarger lagret og oppdatert!",
    "settings.companyUpdated": "Firmainformasjon oppdatert!",
    "settings.orgStructureDesc": "Administrer avdelinger, jobbroller og filialer",
    "settings.uploadImage": "Vennligst last opp en bildefil.",
    "settings.imageTooLarge": "Bildet må være mindre enn 5MB.",
    "settings.cropLogo": "Beskjær logo",

    // Users
    "users.title": "Brukere",
    "users.members": "{count} medlemmer",
    "users.addUser": "Legg til bruker",
    "users.add": "Legg til",
    "users.searchPlaceholder": "Søk brukere eller invitasjoner...",
    "users.selected": "{count} valgt",
    "users.assign": "Tildel",
    "users.remove": "Fjern",
    "users.cancel": "Avbryt",
    "users.allLocations": "Alle lokasjoner",
    "users.allRoles": "Alle roller",
    "users.allDepartments": "Alle avdelinger",
    "users.clearFilters": "Fjern filtre",
    "users.name": "Navn",
    "users.roleName": "Rolle",
    "users.progress": "Fremgang",
    "users.onboarding": "Onboarding",
    "users.lastActive": "Sist aktiv",
    "users.ascending": "Stigende",
    "users.descending": "Synkende",
    "users.selectAll": "Velg alle ({count})",
    "users.noMembers": "Ingen medlemmer funnet",
    "users.noCourses": "Ingen kurs",
    "users.unknownUser": "Ukjent bruker",
    "users.neverLoggedIn": "Aldri logget inn",
    "users.edit": "Rediger",
    "users.protectedUser": "Beskyttet bruker",
    "users.resendLogin": "Send innloggingsinfo på nytt",
    "users.sendReminder": "Send kurspåminnelse",
    "users.removeManager": "Fjern manager",
    "users.makeManager": "Gjør til manager",

    // Profile
    "profile.title": "Min profil",
    "profile.description": "Se og rediger din profilinformasjon",
    "profile.picture": "Profilbilde",
    "profile.pictureDesc": "Last opp et bilde som representerer deg",
    "profile.imageFormat": "JPG, PNG eller GIF. Maks 5MB.",
    "profile.personalInfo": "Personlig informasjon",
    "profile.personalInfoDesc": "Oppdater din kontaktinformasjon",
    "profile.fullName": "Fullt navn",
    "profile.namePlaceholder": "Skriv inn ditt navn",
    "profile.email": "E-post",
    "profile.emailCantChange": "E-post kan ikke endres",
    "profile.phone": "Telefon",
    "profile.organization": "Organisasjon",
    "profile.saveChanges": "Lagre endringer",
    "profile.changePassword": "Endre passord",
    "profile.changePasswordDesc": "Oppdater passordet ditt for økt sikkerhet",
    "profile.newPassword": "Nytt passord",
    "profile.newPasswordPlaceholder": "Skriv inn nytt passord",
    "profile.confirmPassword": "Bekreft nytt passord",
    "profile.confirmPasswordPlaceholder": "Bekreft nytt passord",
    "profile.updatePassword": "Endre passord",
    "profile.profileUpdated": "Profil oppdatert!",
    "profile.profileError": "Kunne ikke oppdatere profil",
    "profile.passwordUpdated": "Passord oppdatert!",
    "profile.passwordMismatch": "Passordene samsvarer ikke",
    "profile.passwordTooShort": "Passordet må være minst 6 tegn",
    "profile.selectImage": "Vennligst velg en bildefil",
    "profile.imageUnder5MB": "Bildet må være under 5MB",
    "profile.pictureUpdated": "Profilbilde oppdatert!",
    "profile.pictureError": "Kunne ikke laste opp bilde",
    "profile.cropPicture": "Beskjær profilbilde",

    // Roles
    "role.superAdmin": "Super Admin",
    "role.superAdminDesc": "Full tilgang til alle organisasjoner og funksjoner",
    "role.manager": "Manager",
    "role.managerDesc": "Administrer brukere, kurs og innhold i denne organisasjonen",
    "role.learner": "Deltaker",
    "role.learnerDesc": "Tilgang til tildelte kurs og spor fremgang",
    "role.admin": "Admin",

    // Course Editor
    "editor.courseNotFound": "Kurs ikke funnet",
    "editor.goBack": "Gå tilbake",
    "editor.elements": "elementer",
    "editor.saveChanges": "Lagre endringer",
    "editor.saveAndFinish": "Lagre og fullfør",
    "editor.finish": "Fullfør",
    "editor.content": "Innhold",
    "editor.details": "Detaljer",
    "editor.targeting": "Targeting",
    "editor.requirements": "Krav",
    "editor.certificate": "Sertifikat",
    "editor.addElement": "Legg til element",
    "editor.addNewElement": "Legg til nytt element",
    "editor.name": "Navn",
    "editor.namePlaceholder": "f.eks. Introduksjon til mattrygghet",
    "editor.videoUrl": "Video-URL (valgfritt)",
    "editor.videoPlaceholder": "f.eks. https://youtube.com/watch?v=...",
    "editor.videoHelp": "YouTube, Vimeo eller Loom",
    "editor.files": "Filer (valgfritt)",
    "editor.dropFiles": "Dra og slipp filer, eller",
    "editor.clickToSelect": "klikk for å velge",
    "editor.fileTypes": "PDF, bilder, video, dokumenter",
    "editor.adding": "Legger til...",
    "editor.noElements": "Ingen elementer ennå. Legg til ditt første element!",
    "editor.expandTip": "Utvid et element for å legge til quizer og dokumenter",
    "editor.quizTip": "Du kan legge til quiz og flere detaljer etterpå",
    "editor.courseTitle": "Kurstittel",
    "editor.courseTitlePlaceholder": "Kurstittel",
    "editor.description": "Beskrivelse",
    "editor.descriptionPlaceholder": "Hva vil deltakerne lære i dette kurset?",
    "editor.coverImage": "Coverbilde",
    "editor.changeImage": "Endre bilde",
    "editor.swap": "Bytt",
    "editor.removeImg": "Fjern",
    "editor.clickUploadCover": "Klikk for å laste opp et coverbilde",
    "editor.coverFormat": "JPG, PNG eller GIF. Maks 5MB.",
    "editor.cropCover": "Beskjær coverbilde",
    "editor.selectImage": "Vennligst velg et bilde",
    "editor.imageUnder5MB": "Bildet må være under 5MB",
    "editor.courseUpdated": "Kurs oppdatert!",
    "editor.courseUpdateError": "Kunne ikke oppdatere kurs",
    "editor.elementAdded": "Element lagt til!",
    "editor.elementAddError": "Kunne ikke legge til element",
    "editor.elementDeleted": "Element slettet",
    "editor.elementUpdated": "Element oppdatert",
    "editor.orderUpdated": "Rekkefølge oppdatert",
    "editor.orderError": "Kunne ikke oppdatere rekkefølge",
    "editor.imageUploaded": "Bilde lastet opp!",
    "editor.imageUploadError": "Kunne ikke laste opp bilde",
    "editor.wizardSkip": "Hopp over",
    "editor.wizardNext": "Neste",
    "editor.wizardFinish": "Fullfør oppsett",
    "editor.wizardBack": "Tilbake",
    "editor.wizardExitLabel": "Avslutt veiviser",
    "editor.wizardStepLabel": "Kursoppsett",
    "editor.wizardOptional": "Valgfritt",
    "editor.wizardStepContent": "Legg til elementer, videoer og dokumenter",
    "editor.wizardStepDetails": "Sett tittel, beskrivelse og forsidebilde",
    "editor.wizardStepTargeting": "Velg hvem kurset skal tildeles",
    "editor.wizardStepRequirements": "Sett krav om andre kurs først",
    "editor.wizardStepCertificate": "Konfigurer sertifisering ved fullføring",

    // MiniQuiz / Campaigns
    // MyCourses
    "myCourses.title": "Mine kurs",
    "myCourses.subtitle": "Følg med på læringsfremdriften din",
    "myCourses.total": "Totalt",
    "myCourses.inProgress": "Pågående",
    "myCourses.completed": "Fullført",
    "myCourses.searchPlaceholder": "Søk i kurs...",
    "myCourses.all": "Alle",
    "myCourses.noMatch": "Ingen kurs samsvarer med filtrene",
    "myCourses.noCoursesYet": "Ingen kurs tildelt ennå",
    "myCourses.lessons": "elementer",
    "myCourses.locked": "Låst",
    "myCourses.requires": "Krever:",
    "myCourses.overallProgress": "Total fremgang",
    "myCourses.coursesCompleted": "kurs fullført",
    "myCourses.continue": "Fortsett",

    // Certificates
    "certificates.title": "Mine sertifikater",
    "certificates.description": "Oversikt over dine opptjente sertifikater",
    "certificates.none": "Ingen sertifikater ennå",
    "certificates.noneDesc": "Fullfør kurs for å motta sertifikater",
    "certificates.valid": "Gyldig",
    "certificates.expired": "Utløpt",
    "certificates.expiringSoon": "Utløper snart",
    "certificates.revoked": "Tilbakekalt",
    "certificates.issued": "Utstedt",
    "certificates.expires": "Utløper",
    "certificates.noExpiry": "Ingen utløpsdato",
    "certificates.view": "Vis sertifikat",
    "certificates.heading": "Kursbevis",
    "certificates.subheading": "Kursbevis",
    "certificates.awardedTo": "Tildelt",
    "certificates.forCompletion": "for fullføring av kurset",
    "certificates.validUntil": "Gyldig til",
    "certificates.certNumber": "Sertifikatnr.",
    "certificates.close": "Lukk",
    "certificates.download": "Last ned",

    "miniquiz.title": "MiniQuiz",
    "miniquiz.subtitle": "Raske quizer og innhold for teamet ditt",
    "miniquiz.new": "Ny MiniQuiz",
    "miniquiz.none": "Ingen MiniQuiz ennå",
    "miniquiz.noneDesc": "Opprett en MiniQuiz for å dele raske quizer og innhold med teamet.",
    "miniquiz.edit": "Rediger MiniQuiz",
    "miniquiz.quizName": "Navn på quiz",
    "miniquiz.quizNamePlaceholder": "f.eks. Nye menyendringer",
    "miniquiz.textContent": "Tekst / innhold",
    "miniquiz.textPlaceholder": "Skriv informasjonen som ansatte skal lese før quizen...",
    "miniquiz.textHelp": "Dette vises som leksjon før quizen",
    "miniquiz.questions": "Spørsmål",
    "miniquiz.addQuestion": "Legg til spørsmål",
    "miniquiz.question": "Spørsmål",
    "miniquiz.writeQuestion": "Skriv spørsmålet...",
    "miniquiz.markCorrect": "Marker som riktig svar",
    "miniquiz.optionPlaceholder": "Svaralternativ",
    "miniquiz.addOption": "+ Legg til svaralternativ",
    "miniquiz.settingsTitle": "Innstillinger",
    "miniquiz.deadline": "Frist (valgfritt)",
    "miniquiz.mandatory": "Obligatorisk",
    "miniquiz.cancel": "Avbryt",
    "miniquiz.saving": "Lagrer...",
    "miniquiz.save": "Lagre",
    "miniquiz.create": "Opprett MiniQuiz",
    "miniquiz.updated": "MiniQuiz oppdatert",
    "miniquiz.created": "MiniQuiz opprettet!",
    "miniquiz.error": "Noe gikk galt",
    "miniquiz.reactivated": "MiniQuiz reaktivert",
    "miniquiz.archived": "MiniQuiz arkivert",
    "miniquiz.statusError": "Kunne ikke oppdatere status",
    "miniquiz.deleted": "MiniQuiz slettet",
    "miniquiz.deleteError": "Kunne ikke slette",
    "miniquiz.course": "Kurs",
    "miniquiz.deadlineLabel": "Frist",

    // Manager Dashboard
    "manager.goodMorning": "God morgen",
    "manager.goodAfternoon": "God ettermiddag",
    "manager.goodEvening": "God kveld",
    "manager.viewProgress": "Se fremgang",
    "manager.inviteTeam": "Inviter team",
    "manager.team": "Team",
    "manager.courses": "Kurs",
    "manager.progress": "Fremgang",
    "manager.completed": "Fullført",
    "manager.reportSent": "Rapport sendt til din e-post!",
    "manager.reportError": "Kunne ikke sende rapport. Sjekk e-postkonfigurasjon.",
    "manager.teamMilestone": "Teammilepæl!",
    "manager.coursesCompletedTotal": "{count} kurs fullført totalt",

    // Activity Feed
    "activity.recentActivity": "Siste aktivitet",
    "activity.viewAll": "Se alle",
    "activity.noActivity": "Ingen aktivitet ennå",
    "activity.activityWillAppear": "Aktivitet vises her når teamet starter",
    "activity.completed": "fullførte",
    "activity.recently": "nylig",

    // Course Stats (Manager)
    "courseStats.title": "Kursstatistikk",
    "courseStats.analytics": "Innsikt",
    "courseStats.noCourses": "Ingen kurs opprettet",
    "courseStats.createToSee": "Opprett kurs for å se statistikk",
    "courseStats.createCourse": "Opprett kurs",
    "courseStats.enrolled": "påmeldt",
    "courseStats.lessons": "elementer",

    // Manager Leaderboard
    "managerLeaderboard.title": "Toppliste",
    "managerLeaderboard.noData": "Ingen data ennå",
    "managerLeaderboard.updatesWhen": "Topplisten oppdateres når teamet fullfører leksjoner",
    "managerLeaderboard.points": "poeng",
    "managerLeaderboard.lessons": "elementer",

    // Team Goals
    "goals.title": "Teammål",
    "goals.completionRate": "100% fullføringsgrad",
    "goals.everyoneTakesQuizzes": "Alle tar quizer",
    "goals.weeklyActivity": "Ukentlig aktivitet",

    // Quick Actions
    "quickActions.title": "Hurtighandlinger",
    "quickActions.inviteTeamMember": "Inviter teammedlem",
    "quickActions.createNewCourse": "Opprett nytt kurs",
    "quickActions.settings": "Innstillinger",
    "quickActions.sendReport": "Send rapport til meg",
    "quickActions.sending": "Sender...",

    // Learner Progress
    "learnerProgress.title": "Fremgang",
    "learnerProgress.description": "Følg individuelle deltakeres fullføringsgrad og aktivitet",
    "learnerProgress.totalLearners": "Totalt deltakere",
    "learnerProgress.avgCompletion": "Gj.snitt fullføring",
    "learnerProgress.inProgress": "Pågående",
    "learnerProgress.completedAll": "Fullført alle",
    "learnerProgress.searchPlaceholder": "Søk etter navn...",
    "learnerProgress.all": "Alle",
    "learnerProgress.active": "Pågående",
    "learnerProgress.completed": "Fullført",
    "learnerProgress.notStarted": "Ikke startet",
    "learnerProgress.allLocations": "Alle lokasjoner",
    "learnerProgress.allRoles": "Alle roller",
    "learnerProgress.noLearners": "Ingen deltakere funnet",
    "learnerProgress.lastActive": "Sist aktiv {time}",
    "learnerProgress.noActivityYet": "Ingen aktivitet ennå",
    "learnerProgress.coursesLabel": "Kurs",
    "learnerProgress.lessonsLabel": "Elementer",

    // Course Analytics
    "analytics.title": "Kursinnsikt",
    "analytics.description": "Følg elementengasjement, frafall og quizresultater",
    "analytics.allCourses": "Alle kurs",
    "analytics.allLocations": "Alle lokasjoner",
    "analytics.allRoles": "Alle roller",
    "analytics.allDepartments": "Alle avdelinger",
    "analytics.export": "Eksporter",
    "analytics.exported": "Data eksportert som CSV",
    "analytics.noCoursesToAnalyze": "Ingen kurs å analysere ennå",
    "analytics.courseOverview": "Kursoversikt",
    "analytics.course": "Kurs",
    "analytics.lessons": "Elementer",
    "analytics.lessonsAndEnrolled": "{lessons} elementer • {enrolled} påmeldt",
    "analytics.completedPercent": "Fullført",
    "analytics.passed": "bestått",
    "analytics.failed": "ikke bestått",
    "analytics.avgTime": "Gj.snitt tid",
    "analytics.lessonBreakdown": "Element for element",
    "analytics.dropOff": "{percent}% frafall",
    "analytics.ofCompleted": "{completed} av {total} fullført",
    "analytics.overview": "Oversikt",
    "analytics.performance": "Ytelse",
    "analytics.totalUsers": "Totalt brukere",
    "analytics.activeUsers": "Aktive brukere (MAU)",
    "analytics.coursesCompleted": "Kurs fullført",
    "analytics.quizPassRate": "Bestått quiz",
    "analytics.enrollmentRate": "Påmeldingsgrad",
    "analytics.completionRate": "Fullføringsgrad",
    "analytics.avgQuizScore": "Gj.snitt quizscore",
    "analytics.onboardingTime": "Estimert kurstid",
    "analytics.onboardingDays": "Tid til fullføring",
    "analytics.mobileUsage": "Mobilbruk",
    "analytics.trainingGaps": "Opplæringsbehov",
    "analytics.lowestCompletion": "Lavest fullføringsgrad",
    "analytics.highestQuizFailure": "Høyest strykprosent",
    "analytics.problemLessons": "Mest utfordrende leksjoner",
    "analytics.completedOf": "fullført",
    "analytics.noQuizData": "Ingen quizdata ennå",
    "analytics.failRate": "stryk",
    "analytics.noData": "Ingen data ennå",
    "analytics.completionLower": "fullført",

    // Venue Comparison
    "venue.locationComparison": "Lokasjonssammenligning",
    "venue.location": "Lokasjon",
    "venue.users": "Brukere",
    "venue.enrollments": "Påmeldinger",
    "venue.completions": "Fullføringer",
    "venue.completionRate": "Fullføringsgrad",
    "venue.quizPassRate": "Bestått quiz",
    "venue.avgScore": "Gj.snitt score",

    // User Detail
    "userDetail.title": "Brukerdetaljer",
    "userDetail.userNotFound": "Bruker ikke funnet",
    "userDetail.notFoundDesc": "Denne brukeren ble ikke funnet i denne organisasjonen.",
    "userDetail.goBack": "Gå tilbake",
    "userDetail.assignCourse": "Tildel kurs",
    "userDetail.issueCert": "Tildel sertifikat",
    "userDetail.edit": "Rediger",
    "userDetail.back": "Tilbake",
    "userDetail.description": "Brukerdetaljer og opplæringsstatus",
    "userDetail.departments": "Avdelinger",
    "userDetail.jobRoles": "Stillinger",
    "userDetail.locations": "Lokasjoner",
    "userDetail.lastLogin": "Siste innlogging",
    "userDetail.neverLoggedIn": "Aldri logget inn",
    "userDetail.memberSince": "Medlem siden",
    "userDetail.coursesAssigned": "Tildelte kurs",
    "userDetail.overallCompletion": "Total fullføring",
    "userDetail.certificates": "Sertifikater",
    "userDetail.badges": "Merker",
    "userDetail.courseProgress": "Kursfremgang",
    "userDetail.noCourses": "Ingen kurs tildelt",
    "userDetail.unknownCourse": "Ukjent kurs",
    "userDetail.lessonsCount": "elementer",
    "userDetail.lastActiveTime": "Sist aktiv",
    "userDetail.completedStatus": "Fullført",
    "userDetail.inProgressStatus": "Pågående",
    "userDetail.notStartedStatus": "Ikke startet",
    "userDetail.quizResults": "Quizresultater",
    "userDetail.noActivity": "Ingen aktivitet registrert",
    "userDetail.activityTimeline": "Aktivitetstidslinje",
    "userDetail.completedLesson": "fullførte",

    // Manage Courses
    "manageCourses.title": "Administrer kurs",
    "manageCourses.createCourse": "Opprett kurs",
    "manageCourses.createNew": "Opprett nytt kurs",
    "manageCourses.titleLabel": "Tittel",
    "manageCourses.descriptionLabel": "Beskrivelse",
    "manageCourses.coverImage": "Coverbilde (valgfritt)",
    "manageCourses.dragDrop": "Dra og slipp eller klikk for å laste opp",
    "manageCourses.creating": "Oppretter...",
    "manageCourses.editCourse": "Rediger kurs",
    "manageCourses.assignCourse": "Tildel kurs",
    "manageCourses.searchPlaceholder": "Søk i kurs...",
    "manageCourses.noCourses": "Ingen kurs opprettet ennå",
    "manageCourses.createFirst": "Opprett ditt første kurs for å komme i gang",
    "manageCourses.deleteCourse": "Slett kurs",
    "manageCourses.deleteConfirm": "Er du sikker på at du vil slette dette kurset? Dette kan ikke angres.",
    "manageCourses.typeToConfirm": "Skriv kurstittelen for å bekrefte",
    "manageCourses.cancelDelete": "Avbryt",
    "manageCourses.deletePermanent": "Slett permanent",
    "manageCourses.sendReminder": "Send påminnelse",
    "manageCourses.reminderSent": "Påminnelse sendt til {count} deltaker(e)",
    "manageCourses.reminderError": "Kunne ikke sende påminnelser. Prøv igjen.",
    "manageCourses.courseTitlePlaceholder": "Kurstittel",
    "manageCourses.courseDescPlaceholder": "Kursbeskrivelse",
    "manageCourses.couldNotCreate": "Kunne ikke opprette kurs",
    "manageCourses.cropCover": "Beskjær coverbilde",
    "manageCourses.lessonsCount": "elementer",
    "manageCourses.enrolledCount": "påmeldt",
    "manageCourses.noDescription": "Ingen beskrivelse",
    "manageCourses.assignUsers": "Tildel brukere",
    "manageCourses.sending": "Sender...",
    "manageCourses.sharedLibrary": "Delt bibliotek",
    "manageCourses.sharedLibraryDesc": "Disse kursene kan tildeles alle organisasjoner",
    "manageCourses.assignToOrg": "Tildel til denne org",
    "manageCourses.courseCreated": "Kurs opprettet!",
    "manageCourses.courseDeleted": "Kurs slettet",
    "manageCourses.courseAssigned": "Kurs tildelt organisasjonen",
    "manageCourses.everyoneCompleted": "Alle har fullført dette kurset!",

    // Time periods
    "timePeriod.allTime": "All tid",
    "timePeriod.today": "I dag",
    "timePeriod.yesterday": "I går",
    "timePeriod.thisWeek": "Denne uken",
    "timePeriod.thisMonth": "Denne måneden",
    "timePeriod.lastMonth": "Forrige måned",
    "timePeriod.last3Months": "Siste 3 måneder",
    "timePeriod.lastYear": "Siste år",
    "timePeriod.customRange": "Egendefinert periode",
    "timePeriod.from": "Fra",
    "timePeriod.to": "Til",

    // User Detail extra
    "userDetail.joinedOrg": "Ble med i organisasjonen",
    "userDetail.assignedCourse": "Tildelt kurs: {name}",
    "userDetail.completedCourse": "Fullførte kurs: {name}",
    "userDetail.completedLessonName": "Fullførte leksjon: {name}",
    "userDetail.quizName": "Quiz: {name}",
    "userDetail.quizScore": "Score: {score}% — {result}",
    "userDetail.passed": "Bestått",
    "userDetail.failed": "Ikke bestått",
    "userDetail.certIssued": "Sertifikat utstedt: {name}",
    "userDetail.badgeEarned": "Merke opptjent: {name}",
    "userDetail.issuedLabel": "Utstedt",
    "userDetail.expiresLabel": "Utløper",
    "userDetail.documentsReview": "Dokumenter til gjennomgang",
    "userDetail.confirmedRead": "Bekreftet lest",
    "userDetail.openedNotConfirmed": "Åpnet – ikke bekreftet",
    "userDetail.notOpened": "Ikke åpnet",
    "userDetail.readStatus": "Lest",
    "userDetail.openedStatus": "Åpnet",
    "userDetail.unreadStatus": "Ulest",
    "userDetail.unknownUser": "Ukjent bruker",
    "userDetail.progressLabel": "Fremgang",

    // Landing page
     "landing.nav.problems": "Problemer",
     "landing.nav.features": "Funksjoner",
     "landing.nav.pricing": "Pris",
     "landing.nav.caseStudy": "Kundecase",
     "landing.nav.guide": "Innsikt",
    "landing.nav.login": "Logg inn",
    "landing.nav.bookDemo": "Book en demo",
    "landing.hero.badge": "Norsk digital opplæringsplattform med AI",
    "landing.hero.title": "Den beste investeringen du gjør er i dine medarbeidere",
    "landing.hero.subtitle": "Trainly gjør det enkelt å lage, dele og følge opp opplæring, slik at dine medarbeidere presterer bedre fra dag én.",
    "landing.hero.cta": "Book en demo",
    "landing.hero.cardAdmin": "Lag kurs og quiz med AI",
    "landing.hero.cardControl": "Full innsikt i opplæringen",
    "landing.hero.cardSafety": "Tryggere ansatte som presterer bedre",
    "landing.socialProof": "Vi jobber med bedrifter som bryr seg om resultater:",
    "landing.benefits.badge": "Fordeler med Trainly LMS",
    "landing.benefits.title": "Full kontroll på opplæring",
    "landing.benefits.subtitle": "Med Trainly får alle ansatte lik, kvalitetssikret opplæring uansett lokasjon eller hvem som er på jobb.",
    "landing.benefits.sameTraining": "Spar tid og penger på opplæring",
    "landing.benefits.sameTrainingDesc": "Slutt å bruke på manuell oppfølging og tidskrevende oppgaver. Med strukturert og digital opplæring sparer du både tid, ressurser og unødvendige kostnader.",
    "landing.benefits.tag1": "Effektivisering",
    "landing.benefits.tag2": "Kostnadsbesparende",
    "landing.benefits.tag3": "Automatisert",
    "landing.benefits.tag4": "Resultater",
    "landing.benefits.documented": "Tryggere ansatte, bedre resultater",
    "landing.benefits.documentedDesc": "Ansatte som er godt opplært, jobber mer selvstendig og gjør færre feil. Det gir høyere kvalitet, bedre kundeopplevelser og en mer profesjonell drift.",
    "landing.benefits.overview": "Nye ansatte operative på timer, ikke uker",
    "landing.benefits.overviewDesc": "Gi nyansatte en strukturert og tydelig start fra første dag. De møter forberedt, føler seg tryggere og bidrar raskere til verdiskaping.",
    "landing.benefits.saferResults": "Innsikt i sanntid med dashbord og rapporter",
    "landing.benefits.saferResultsDesc": "Få full oversikt over gjennomføring, sertifikater og kompetanse i hele organisasjonen. Ta beslutninger basert på data, ikke antakelser.",
    "landing.benefits.fewerErrors": "Full oversikt over hele organisasjonen",
    "landing.benefits.higherQuality": "Sammenlign avdeling mot avdeling",
    "landing.benefits.betterSatisfaction": "Engasjement og gjennomføring over tid",
    "landing.benefits.lowerTurnover": "Se ansatte som utmerker seg!",
    "landing.beforeAfter.title1": "Gå fra",
    "landing.beforeAfter.chaos": "kaos",
    "landing.beforeAfter.to": "til",
    "landing.beforeAfter.control": "kontroll",
    "landing.beforeAfter.subtitle": "Se forskjellen mellom tradisjonell opplæring og det du får med Trainly.",
    "landing.beforeAfter.tryFree": "Prøv gratis",
    "landing.beforeAfter.traditional": "Tradisjonell opplæring",
    "landing.before.1": "Opplæring spredt på e-post, permer og muntlig",
    "landing.before.2": "Ulik opplæring avhengig av hvem som lærer opp",
    "landing.before.3": "Kostbart med fysiske kurs og manuell oppfølging",
    "landing.before.4": "Nye ansatte bruker uker på å bli operative",
    "landing.before.5": "Ingen oversikt over gjennomført opplæring",
    "landing.before.6": "Kun tilgjengelig på kontoret i arbeidstiden",
    "landing.before.7": "Rutineendringer må kommuniseres manuelt",
    "landing.before.8": "Ujevnt kompetansenivå gir feil og risiko",
    "landing.after.1": "Alt samlet på ett sted — kurs, rutiner og tester",
    "landing.after.2": "Lik opplæring for alle, uansett sted eller tid",
    "landing.after.3": "Spar tid og penger på opplæring",
    "landing.after.4": "Nye ansatte operative på dager, ikke uker",
    "landing.after.5": "Full sporbarhet for HMS og internkontroll",
    "landing.after.6": "Tilgjengelig på mobil, nettbrett og PC",
    "landing.after.7": "Oppdater én gang — alle får siste versjon",
    "landing.after.8": "Tryggere ansatte, færre feil og bedre drift",
     "landing.pricing.badge": "Se vår enkle prismodell",
     "landing.pricing.title": "Målet vårt er at du skal lykkes.",
     "landing.pricing.subtitle": "Velg planen som passer for din bedrift. Ingen skjulte kostnader og ingen bindingstid.",
    "landing.pricing.freeOnboarding": "Gratis onboarding inkludert!",
    "landing.pricing.noCommitment": "Ingen bindingstid",
    "landing.pricing.mostPopular": "Mest populær",
    "landing.pricing.contactUs": "Kontakt oss",
    "landing.pricing.start": "Start",
    "landing.pricing.startDesc": "For mindre virksomheter som ønsker strukturert og enkel opplæring.",
    "landing.pricing.startSub": "",
    "landing.pricing.startCapacity": "Opp til 50 medarbeidere",
    "landing.pricing.startFooter": "Perfekt for enkeltstående avdelinger og mindre team.",
    "landing.pricing.startCta": "Kom i gang",
    "landing.pricing.growth": "Vekst",
    "landing.pricing.growthDesc": "For virksomheter med flere avdelinger og behov for mer innsikt og kontroll.",
    "landing.pricing.growthSub": "",
    "landing.pricing.growthCapacity": "Opp til 200 medarbeidere",
    "landing.pricing.growthFooter": "Perfekt for kjeder og virksomheter i vekst.",
    "landing.pricing.enterprise": "Stor",
    "landing.pricing.enterpriseDesc": "For større organisasjoner og kjeder med spesifikke behov.",
    "landing.pricing.enterpriseSub": "",
    "landing.pricing.enterprisePrice": "Kontakt oss",
    "landing.pricing.enterpriseFooter": "Ta kontakt for et tilpasset pristilbud.",
    "landing.pricing.enterpriseCta": "Kontakt salg",
    "landing.pricing.f.unlimited": "Ubegrenset antall kurs",
    "landing.pricing.f.courseBuilder": "Kursbygger med video, tekst og dokumenter",
    "landing.pricing.f.quizzes": "Tester og quiz med automatisk vurdering",
    "landing.pricing.f.roleAssign": "Rollebasert tildeling av opplæring",
    "landing.pricing.f.tracking": "Oversikt over gjennomføring",
    "landing.pricing.f.certs": "Sertifikater og påminnelser",
    "landing.pricing.f.mobile": "Mobil og desktop",
    "landing.pricing.f.emailSupport": "E-post support",
    "landing.pricing.f.allStart": "Alt i Start, pluss:",
    "landing.pricing.f.deptLocation": "Avdelings- og lokasjonsstruktur",
    "landing.pricing.f.advInsight": "Avansert innsikt og rapporter",
    "landing.pricing.f.autoFollow": "Automatisk oppfølging og varsler",
    "landing.pricing.f.multiAdmin": "Flere administratorroller",
    "landing.pricing.f.prioSupport": "Prioritert support",
    "landing.pricing.f.integrations": "Mulighet for integrasjoner",
    "landing.pricing.f.allGrowth": "Alt i Vekst, pluss:",
    "landing.pricing.f.custom": "Skreddersydd oppsett",
    "landing.pricing.f.ownOnboard": "Egen onboarding-rådgiver",
    "landing.pricing.f.integrationsEnt": "Integrasjoner (HR, SSO, API)",
    "landing.pricing.f.advReporting": "Avansert rapportering og eksport",
    "landing.pricing.f.dedicated": "Dedikert kundesuksess-kontakt",
    "landing.pricing.f.aiCourses": "AI-genererte kurs og quiz",
    "landing.pricing.f.whiteLabel": "Egen branding og farger",
    "landing.pricing.f.multiOrg": "Flere lokasjoner / selskaper",
    "landing.pricing.f.sla": "SLA og prioritert responstid",
    "landing.faq.title": "Ofte stilte spørsmål om Trainly",
    "landing.faq.subtitle": "Alt du trenger å vite om vår opplæringsplattform for bedrifter",
    "landing.faq.q1": "Hva er et LMS (Learning Management System)?",
    "landing.faq.a1": "Et LMS, eller Learning Management System, er en digital plattform som lar bedrifter opprette, distribuere og følge opp opplæring av ansatte. I stedet for fysiske kurs, permer og Excel-lister samler et LMS alt på ett sted – kurs, quiz, sertifiseringer og rapportering. Trainly er et LMS utviklet spesielt for små og mellomstore bedrifter i Norge, og gjør det enkelt å komme i gang med digital opplæring uten teknisk kompetanse.",
    "landing.faq.q2": "Hva er Trainly, og hvem passer det for?",
    "landing.faq.a2": "Trainly er en norsk opplæringsplattform (LMS) designet for bedrifter med 10–300 ansatte. Plattformen passer for alle bransjer, men er spesielt populær blant restauranter, kjedevirksomheter, transport, bygg og servicebedrifter. Trainly gjør det enkelt å lage kurs, tildele opplæring basert på rolle og lokasjon, og følge opp fremgang – alt i én løsning.",
    "landing.faq.q3": "Hva skiller Trainly fra andre LMS-systemer?",
    "landing.faq.a3": "Mange LMS-systemer er bygget for store konsern og krever lang implementeringstid og teknisk kompetanse. Trainly er laget for å være enkelt og brukervennlig fra dag én. Du kan lage kurs med AI på minutter, tildele opplæring automatisk basert på rolle og avdeling, og få full oversikt over fremgang uten komplisert oppsett. I tillegg er det ingen bindingstid.",
    "landing.faq.q4": "Hvor raskt kan vi komme i gang med Trainly?",
    "landing.faq.a4": "De fleste bedrifter er i gang i løpet av noen dager. Vi hjelper dere med oppsett, kursstruktur og innhold. Med AI-verktøyet kan dere lage profesjonelle kurs og quiz på minutter ved å skrive inn en prosedyre eller laste opp et dokument. Dere trenger ingen teknisk bakgrunn for å bruke Trainly.",
    "landing.faq.q5": "Hva koster Trainly?",
    "landing.faq.a5": "Trainly har fleksible planer som passer ulike bedriftsstørrelser. Det er ingen oppstartskostnad og ingen bindingstid – du betaler per bruker per måned. Kontakt oss for en uforpliktende pris basert på antall ansatte og behov, eller sjekk vår prisside for mer informasjon.",
    "landing.faq.q6": "Kan Trainly brukes til onboarding av nye ansatte?",
    "landing.faq.a6": "Ja, onboarding er en av de mest brukte funksjonene i Trainly. Nye ansatte får automatisk tildelt riktig opplæringsløp fra dag én, basert på rolle, avdeling og lokasjon. Dette sikrer at alle får samme kvalitet på opplæringen, uavhengig av hvem som er på jobb. Bedrifter som bruker Trainly til onboarding opplever raskere produktivitet og tryggere nyansatte.",
    "landing.faq.q7": "Fungerer Trainly for restauranter og kjedevirksomheter?",
    "landing.faq.a7": "Ja, Trainly er ideelt for restauranter, kafeer og kjedevirksomheter. Dere kan lage standardiserte kurs for matservering, hygiene, HMS og kundebehandling, og tildele dem automatisk til ansatte på tvers av lokasjoner. Ledere får oversikt over hvem som har gjennomført opplæring på hver enkelt filial, og ansatte kan ta kursene på mobilen når det passer dem.",
    "landing.faq.q8": "Kan ansatte gjennomføre opplæring på mobil?",
    "landing.faq.a8": "Ja, Trainly fungerer på mobil, nettbrett og PC. Ansatte kan gjennomføre kurs, ta quiz og lese dokumenter fra enheten de foretrekker. Dette er spesielt nyttig for ansatte som jobber i felt, på gulvet eller i skift – de trenger ikke sitte ved en PC for å fullføre opplæringen.",
    "landing.faq.q9": "Hvordan fungerer AI-kursbyggeren i Trainly?",
    "landing.faq.a9": "Trainly har en innebygd AI som kan generere komplette kurs og quiz basert på tekst eller dokumenter dere laster opp. Skriv inn en prosedyre, et tema eller last opp en PDF, og AI-en lager et strukturert kurs med leksjoner og spørsmål på minutter. Dere kan deretter redigere og tilpasse innholdet. Dette sparer betydelig tid sammenlignet med å lage alt fra bunnen av.",
    "landing.faq.q10": "Kan vi tildele kurs basert på rolle, avdeling og lokasjon?",
    "landing.faq.a10": "Ja. Trainly lar dere sette opp automatisk tildeling av kurs basert på roller, avdelinger og lokasjoner. En servitør i Bergen og en kokk i Oslo kan automatisk få forskjellig opplæring som er relevant for deres stilling. Når en ny ansatt legges inn, får de automatisk riktige kurs uten manuelt arbeid.",
    "landing.faq.q11": "Hvordan hjelper Trainly med HMS og compliance?",
    "landing.faq.a11": "Trainly gjør det enkelt å dokumentere at alle ansatte har fått nødvendig opplæring innen HMS, internkontroll og bransjekrav. Kurs kan settes som obligatoriske med krav til bestått score. Sertifikater følges opp automatisk med varsler før utløp. Ved tilsyn kan dere hente ut full dokumentasjon med ett klikk – hvem har gjennomført hva, når og med hvilket resultat.",
    "landing.faq.q12": "Hvordan følger vi opp ansatte som ikke har fullført opplæring?",
    "landing.faq.a12": "Trainly sender automatiske påminnelser til ansatte som ikke har fullført obligatorisk opplæring. Ledere får oversikt i dashbordet over hvem som mangler kurs, og kan sende ekstra påminnelser med ett klikk. Dere slipper å holde styr på dette manuelt.",
    "landing.faq.q13": "Er det bindingstid på Trainly?",
    "landing.faq.a13": "Nei, det er ingen bindingstid. Du kan når som helst avslutte abonnementet. Vi tror på at produktet skal tale for seg selv – ikke lange kontrakter. De fleste kunder blir fordi de opplever reell verdi fra første uke.",
    "landing.faq.q14": "Kan vi få en gratis demo av Trainly?",
    "landing.faq.a14": "Ja! Du kan booke en gratis, uforpliktende demo der vi viser deg hvordan Trainly fungerer for din type bedrift. Vi ser på dine behov og viser konkret hvordan plattformen kan hjelpe dere med opplæring, onboarding og oppfølging av ansatte.",
    "landing.faq.q15": "Hva er forskjellen mellom Trainly og gratis LMS-løsninger?",
    "landing.faq.a15": "Gratis LMS-løsninger krever ofte teknisk kompetanse for oppsett, mangler norsk support, og har begrenset funksjonalitet for oppfølging og rapportering. Trainly er ferdig konfigurert, har norsk grensesnitt og support, AI-kursbygging, automatisk tildeling og komplett rapportering – uten at dere trenger teknisk bakgrunn. Dere sparer tid og får en løsning som faktisk blir brukt.",
    "landing.faq.q16": "Kan Trainly brukes til opplæring på tvers av flere lokasjoner?",
    "landing.faq.a16": "Ja, Trainly er bygget for bedrifter med flere avdelinger og lokasjoner. Dere kan sammenligne gjennomføringsgrad mellom filialer, tildele lokasjonsspesifikk opplæring, og sikre at alle ansatte får den opplæringen de trenger – uansett hvor de jobber. Ledere på hver lokasjon kan følge opp sitt team, mens hovedkontoret får full oversikt.",
    "landing.testimonials.label": "Våre kunder",
    "landing.testimonials.title": "Resultater som teller",
    "landing.testimonials.readCase": "Les hele caset",
    "landing.testimonials.roleCity": "Daglig leder, CITY AS",
    "landing.testimonials.roleIsdalen": "Daglig leder, Isdalen Green Transport",
    "landing.testimonials.quoteCity": "Med Trainly har vi fått strukturert og digitalisert opplæringen vår. De ansatte er tryggere, mer selvstendige og leverer høyere kvalitet i hverdagen. Vi merker at ansatte føler seg godt forberedt, og det gjør dem til enda flinkere medarbeidere.",
    "landing.testimonials.quoteIsdalen": "Trainly har gitt oss full innsikt og kontroll på opplæringen. Alle kursene ble opprettet raskt og enkelt ved hjelp av den innebygde AI-funksjonen, og vi har nå dokumentasjon og status tilgjengelig til enhver tid. Det sparer oss for tid og gir oss trygghet i driften.",
    "landing.testimonials.content": "Etter at vi tok i bruk Trainly har vi redusert kostnadene våre, men viktigst av alt: opplæringen har nådd et helt nytt nivå. De ansatte er tryggere i rollene sine, og vi har full oversikt og kontroll – uten ekstra administrasjon.",
    "landing.testimonials.cta": "Les om hvordan CITY AS gikk fra permer til full digital kontroll",
    "landing.guide.title": "Få tips til smartere opplæring",
    "landing.guide.subtitle": "Vår guide viser deg steg for steg hvordan du setter opp Trainly, lager kurs og inviterer ansatte. Alt du trenger for en rask og trygg oppstart.",
    "landing.guide.seeAll": "Se alle våre guider",
    "landing.guide.post1": "Hva er et LMS – og hvorfor trenger norske bedrifter det nå?",
    "landing.guide.post2": "7 tegn på at din bedrift trenger et system for opplæring av ansatte",
    "landing.guide.post3": "Fra Excel og perm til full kontroll: Digitaliser opplæringen i bedriften",
    "landing.footer.description": "Moderne opplæringsplattform for bedrifter. Vi gjør intern opplæring enkelt, sporbart og engasjerende.",
    "landing.footer.navigation": "Navigasjon",
    "landing.footer.pages": "Sider",
    "landing.footer.contact": "Kontakt",
    "landing.footer.bookMeeting": "Book et møte",
    "landing.footer.privacy": "Personvern",
    "landing.footer.madeWith": "Laget med ❤️ av",
    "landing.footer.light": "Lyst",
    "landing.footer.dark": "Mørk",

    "landing.nav.home": "Hjem",
    "landing.nav.contact": "Kontakt",
    "landing.mockup.title": "er tilgjengelig der du er.",
    "landing.mockup.desc": "Ansatte gjennomfører kurs, tar quizer og holder sertifiseringer oppdatert direkte fra mobil eller PC. Ledere får full innsikt og kontroll, akkurat der de er.",
    "landing.mockup.cta": "Kom i gang",
    "landing.mockup.features": "Se alle funksjoner",
    "landing.mockup.greeting": "Hei, Sara 👋",
    "landing.mockup.myCourses": "Mine kurs",
    "landing.mockup.totalProgress": "Totalfremdrift",
    "landing.mockup.coursesOf": "5 av 7 kurs fullført",
    "landing.mockup.ongoing": "Pågående",
    "landing.mockup.newCert": "Nytt kursbevis!",
    "landing.mockup.certDone": "Brannvern fullført",
    "landing.mockup.courseCompleted": "Kurs fullført!",
    "landing.mockup.courseCompletedSub": "Brannvern ✓",
    "landing.mockup.quizPassed": "Quiz bestått",
    "landing.mockup.quizScore": "Score: 95%",
    "landing.mockup.tabCourses": "Kurs",
    "landing.mockup.tabCerts": "Bevis",
    "landing.mockup.tabGoals": "Mål",
    "landing.mockup.tabProfile": "Profil",
    "landing.problems.title": "Utfordringer vi",
    "landing.problems.titleItalic": "løser",
    "landing.problems.subtitle": "Med Trainly blir opplæring en styrke og ikke en utfordring. Enkelt å bruke, enkelt å følge opp, enkelt å dokumentere.",
    "landing.problems.commonChallenges": "Vanlige utfordringer",
    "landing.problems.tryFree": "Prøv gratis",
    "landing.problems.p1": "Det tar for lang tid å lage kurs og quiz",
    "landing.problems.p2": "Opplæringen ligger spredt i e-post og permer",
    "landing.problems.p3": "Ulik kvalitet avhengig av hvem som lærer opp",
    "landing.problems.p4": "Nyansatte bruker lang tid på å bli operative",
    "landing.problems.p5": "Ingen samlet oversikt over fullført opplæring",
    "landing.problems.p6": "Mangler innsikt i ansattes kompetansenivå",
    "landing.problems.p7": "Høye kostnader til fysiske kurs og samlinger",
    "landing.problems.p8": "For mye manuell oppfølging fra ledere",
    "landing.problems.p9": "Rutineendringer kommuniseres manuelt",
    "landing.problems.p10": "Ujevnt kompetansenivå gir feil og risiko",
    "landing.problems.p11": "Vanskelig å dokumentere ved tilsyn",
    "landing.problems.p12": "Vekst gjør opplæringen vanskeligere å styre",
    "landing.problems.p13": "Kunnskap forsvinner når ansatte slutter",
    "landing.problems.s1": "Lag kurs og quiz på minutter med AI",
    "landing.problems.s2": "Alt samlet på ett sted! Kurs, onboarding og tester",
    "landing.problems.s3": "Lik opplæring for alle, uansett språk, sted eller tid",
    "landing.problems.s4": "Nye ansatte operative på timer, ikke uker",
    "landing.problems.s5": "Full sporbarhet for HMS og internkontroll",
    "landing.problems.s6": "Innsikt i sanntid med dashbord og rapporter",
    "landing.problems.s7": "Spar tid og penger på opplæring",
    "landing.problems.s8": "Automatisert tildeling og påminnelser",
    "landing.problems.s9": "Oppdater et sted, alle får siste versjon",
    "landing.problems.s10": "Tryggere ansatte, færre feil og bedre drift",
    "landing.problems.s11": "Dokumentasjon alltid klart ved tilsyn",
    "landing.problems.s12": "Skalerer med bedriften, fra 5 til 5000+ ansatte",
    "landing.problems.s13": "Kunnskap bevart i systemet",
    "landing.funksjoner.badge": "Et komplett LMS",
    "landing.funksjoner.title": "Et komplett opplæringssystem på en enkel måte",
    "landing.funksjoner.subtitle": "Skalerer med virksomheten. Intuitivt fra første klikk. Lag kurs, quiz og struktur uten teknisk kompetanse.",
    "landing.funksjoner.ctaTitle": "Klar til å komme i gang?",
    "landing.funksjoner.ctaSubtitle": "Prøv gratis i 14 dager — ingen kredittkort nødvendig.",
    "landing.funksjoner.ctaButton": "Kom i gang",
    "landing.funksjoner.ctaPricing": "Se priser",
    "landing.funksjoner.f1.title": "Kurs, quiz og kunnskapstester",
    "landing.funksjoner.f1.desc": "Kombiner video, tekst, PDF og bilder med quiz, tester og sjekklister. AI kan generere spørsmål for deg automatisk.",
    "landing.funksjoner.f2.title": "AI som sparer deg tid",
    "landing.funksjoner.f2.desc": "Lag kurs basert på eksisterende tekst, generer quiz automatisk og forenkle innhold. Spar timer hver uke på manuell oppfølging.",
    "landing.funksjoner.f3.title": "Tildel etter rolle, avdeling og lokasjon",
    "landing.funksjoner.f3.desc": "Ansatte med flere roller får riktig opplæring automatisk. Spesielt nyttig for kjeder med mange lokasjoner.",
    "landing.funksjoner.f4.title": "Sertifiseringer og utløpsvarsler",
    "landing.funksjoner.f4.desc": "Sertifikater ved fullført kurs, utløpsdatoer og automatiske varsler. 100% compliance-kontroll ved tilsyn og revisjon.",
    "landing.funksjoner.f5.title": "Innsikt, analyse og fremgang",
    "landing.funksjoner.f5.desc": "Spor hvem som har fullført, sammenlign lokasjoner og avdelinger, og identifiser kompetansegap — alt fra ett dashbord i sanntid.",
    "landing.funksjoner.f6.title": "Påminnelser, kampanjer og oppfølging",
    "landing.funksjoner.f6.desc": "Automatiske varsler om ufullført opplæring og utløpende sertifiseringer. Send målrettede kampanjer til spesifikke grupper med deadlines.",
    "landing.funksjoner.f7.title": "Enkel å bruke — rask oppstart",
    "landing.funksjoner.f7.desc": "Rent grensesnitt som ansatte forstår med én gang. Vi hjelper deg å komme i gang raskt uten frustrasjon.",
    "landing.funksjoner.f8.title": "Onboarding av nyansatte",
    "landing.funksjoner.f8.desc": "Strukturerte sjekklister og dokumenter for nye ansatte. Sørg for at alle får samme gode start — uansett lokasjon.",
    "landing.funksjoner.f9.title": "Eget subdomene for organisasjonen",
    "landing.funksjoner.f9.desc": "Gi ansatte en enkel innlogging via eget subdomene. Merkevaren din, ditt oppsett, dine farger.",
    "landing.funksjoner.f10.title": "Gamification og motivasjon",
    "landing.funksjoner.f10.desc": "Badges, leaderboard og fremgangsvisning motiverer ansatte til å fullføre opplæring og holde seg oppdatert.",
    "landing.funksjoner.f11.title": "Kursbibliotek og gjenbruk",
    "landing.funksjoner.f11.desc": "Del kurs på tvers av lokasjoner og organisasjoner. Bygg et bibliotek av innhold som kan gjenbrukes.",
    "landing.funksjoner.f12.title": "Flerlokasjon og kjedestyring",
    "landing.funksjoner.f12.desc": "Administrer opplæring på tvers av mange lokasjoner fra ett sted. Perfekt for restaurantkjeder, butikker og servicebedrifter.",
    "landing.funksjoner.f13.title": "Rollebasert tilgangskontroll",
    "landing.funksjoner.f13.desc": "Admin, leder og ansatt ser kun det de trenger. Deleger ansvar uten å miste kontroll.",
    "landing.funksjoner.f14.title": "32+ språk, utviklet i Norge",
    "landing.funksjoner.f14.desc": "Tilgjengelig på over 32 språk med norsk support. Utviklet og driftet i Norge for trygg datalagring og rask hjelp.",
    "landing.case.badge": "Hva sier våre kunder?",
    "landing.case.title": "Ekte resultater fra ekte bedrifter",
    "landing.case.subtitle": "Se hvordan Trainly skaper målbare resultater for norske bedrifter.",
    "landing.case.challenge": "Utfordringen",
    "landing.case.solution": "Løsningen",
    "landing.case.results": "Resultater",
    "landing.case.city.tag": "Full digital kontroll",
    "landing.case.city.title": "Fra permer og PDF-er til full digital kontroll",
    "landing.case.city.desc": "CITY AS hadde behov for å digitalisere opplæring og internkontroll. Med Trainly gikk de fra fysiske permer og PDF-er til en heldigital løsning med full oversikt.",
    "landing.case.city.quote": "Med Trainly har vi fått strukturert og digitalisert opplæringen vår. De ansatte er tryggere, mer selvstendige og leverer høyere kvalitet i hverdagen. Vi merker at ansatte føler seg godt forberedt, og det gjør dem til enda flinkere medarbeidere.",
    "landing.case.city.stat1": "Raskere onboarding",
    "landing.case.city.stat2": "Sporbar opplæring",
    "landing.case.city.stat3": "Reduserte kostnader",
    "landing.case.city.stat4": "Mindre administrasjon",
    "landing.case.city.c1": "Opplæring lå i PDF-er, e-poster og permer",
    "landing.case.city.c2": "Ingen oversikt over hvem som hadde gjennomført hva",
    "landing.case.city.c3": "Tidkrevende onboarding av nye ansatte",
    "landing.case.city.c4": "Vanskelig å oppdatere materiell raskt",
    "landing.case.city.s1": "AI-drevet opplæringsplattform med automatisk sporing",
    "landing.case.city.s2": "Digitalisering av alt opplæringsmateriell",
    "landing.case.city.s3": "Automatiske påminnelser og oppfølging",
    "landing.case.city.s4": "Dashboards med sanntidsoversikt for ledere",
    "landing.case.city.r1": "80–90% raskere onboarding av nye ansatte",
    "landing.case.city.r2": "Full dokumentasjon ved kontroll og revisjon",
    "landing.case.city.r3": "Mindre administrasjon for ledelsen",
    "landing.case.city.r4": "Tryggere ansatte med bedre opplæring",
    "landing.case.isdalen.tag": "Effektiv sjåføropplæring",
    "landing.case.isdalen.title": "Fra tidkrevende oppfølging til sømløs digital opplæring",
    "landing.case.isdalen.desc": "Isdalen Green Transport trengte en løsning for å standardisere opplæringen av sjåfører på tvers av avdelinger. Med Trainly fikk de full kontroll og sparte betydelig tid.",
    "landing.case.isdalen.quote": "Trainly har gitt oss full innsikt og kontroll på opplæringen. Alle kursene ble opprettet raskt og enkelt ved hjelp av den innebygde AI-funksjonen, og vi har nå dokumentasjon og status tilgjengelig til enhver tid. Det sparer oss for tid og gir oss trygghet i driften.",
    "landing.case.isdalen.stat1": "Raskere onboarding",
    "landing.case.isdalen.stat2": "Sporbar opplæring",
    "landing.case.isdalen.stat3": "Mindre administrasjon",
    "landing.case.isdalen.stat4": "Reduserte kostnader",
    "landing.case.isdalen.c1": "Ulik opplæring avhengig av avdeling og instruktør",
    "landing.case.isdalen.c2": "Manuell oppfølging av sjåfører tok for mye tid",
    "landing.case.isdalen.c3": "Vanskelig å dokumentere gjennomført opplæring",
    "landing.case.isdalen.c4": "Nye sjåfører brukte lang tid på å bli operative",
    "landing.case.isdalen.s1": "Standardisert digital opplæring for alle sjåfører",
    "landing.case.isdalen.s2": "Automatisk sporing av fremgang og fullføring",
    "landing.case.isdalen.s3": "Kurs tilgjengelig på mobil — perfekt for sjåfører",
    "landing.case.isdalen.s4": "Sertifiseringer med automatiske utløpsvarsler",
    "landing.case.isdalen.r1": "70% raskere onboarding av nye sjåfører",
    "landing.case.isdalen.r2": "Full sporbarhet ved tilsyn og revisjon",
    "landing.case.isdalen.r3": "40% mindre tid brukt på administrasjon",
    "landing.case.isdalen.r4": "Høyere kompetansenivå på tvers av avdelinger",
    "landing.case.ctaTitle": "Vil du oppnå lignende resultater?",
    "landing.case.ctaSubtitle": "Se hvordan Trainly kan effektivisere opplæringen i din bedrift – uten forpliktelser.",
    "landing.case.ctaButton": "Book en demo",
    "landing.case.ctaPricing": "Se priser",
    "landing.contact.title": "Kontakt oss",
    "landing.contact.subtitle": "Har du spørsmål eller vil du booke en demo? Fyll ut skjemaet så tar vi kontakt innen 24 timer.",
    "landing.contact.email": "E-post",
    "landing.contact.phone": "Telefon",
    "landing.contact.bookDemo": "Book en demo med oss",
    "landing.contact.name": "Navn",
    "landing.contact.namePlaceholder": "Ditt navn",
    "landing.contact.emailPlaceholder": "din@epost.no",
    "landing.contact.company": "Bedrift",
    "landing.contact.companyPlaceholder": "Bedriftens navn",
    "landing.contact.message": "Melding",
    "landing.contact.messagePlaceholder": "Fortell oss om dine behov...",
    "landing.contact.success": "✅ Takk for din henvendelse! Vi tar kontakt innen 24 timer.",
    "landing.contact.sending": "Sender...",
    "landing.contact.send": "Send melding",
    "landing.guidePage.badge": "Lær mer om opplæring",
    "landing.guidePage.title": "Guide til",
    "landing.guidePage.titleHighlight": "bedre opplæring",
    "landing.guidePage.subtitle": "Praktiske artikler om hvordan du trener ansatte raskere, reduserer turnover og sikrer compliance i restaurant- og serveringsbransjen.",
    "landing.guidePage.ctaTitle": "Vil du gjøre opplæring enklere?",
    "landing.guidePage.ctaSubtitle": "Trainly gjør det enkelt å lage kurs, trene ansatte og holde styr på sertifiseringer — alt i ett system.",
    "landing.guidePage.ctaButton": "Book en gratis demo",
    "landing.guidePage.backToGuides": "Tilbake til guider",
    "landing.guidePage.readArticle": "Les artikkel",
    "landing.guidePage.improveCta": "Klar til å forbedre opplæringen?",
    "landing.guidePage.improveCtaSub": "Start med Trainly i dag og se resultater på dager, ikke måneder.",
    "landing.guidePage.bookDemo": "Book en demo",

    // Manager widgets
    "widget.missingCourses": "Mangler kurs",
    "widget.drivers": "brukere",
    "widget.overdueDeadlines": "Forfalte frister",
    "widget.daysOverdue": "d over",
    "widget.competencyCerts": "Kompetanse & sertifikater",
    "widget.expired": "Utløpt",
    "widget.expiringSoon": "Utløper snart",
    "widget.onboardingStatus": "Onboarding-status",
    "widget.missing": "mangler",
    "widget.notStarted": "Ikke startet",
    "widget.inProgress": "Under arbeid",
    "widget.courseStatus": "Kursstatus",
    "widget.incomplete": "ufullført",
    "widget.allCompleted": "Alle har fullført sine kurs!",
    "widget.coursesCompleted": "kurs fullført",
    "widget.more": "til",

    // Landing social proof
    "landing.hero.socialProof": "Vi jobber med bedrifter som har kontroll!",

    // Hero floating cards
    "landing.hero.floatSetup": "Vi setter opp for deg",
    "landing.hero.floatSetupDesc": "La oss lage kurs og quiz automatisk med AI, eller last opp eget materiale.",
    "landing.hero.floatInsight": "Innsikt",
    "landing.hero.floatInsightDesc": "Sanntidsoversikt per ansatt på alle avdelinger.",
    "landing.hero.floatOnboarding": "Raskere onboarding",
    "landing.hero.floatOnboardingDesc": "Ansatte som er produktive fra dag én.",
  },
  en: {
    // Sidebar
    "nav.dashboard": "Dashboard",
    "nav.courses": "My courses",
    "nav.manageCourses": "Manage courses",
    "nav.certificates": "Certificates",
    "nav.users": "Users",
    "nav.onboarding": "Onboarding",
    "nav.analytics": "Insight",
    "nav.progress": "Progress",
    "nav.orgStructure": "Organization Structure",
    "nav.settings": "Settings",
    "nav.profile": "Profile",
    "nav.signOut": "Sign Out",
    "nav.viewAs": "View as",
    "nav.faq": "FAQ",
    "nav.campaigns": "MiniQuiz",
    "nav.lightTheme": "Light",
    "nav.darkTheme": "Dark",
    "nav.hideMenu": "Hide menu",
    "nav.switchLang": "Switch to Norwegian",
    "nav.learningPaths": "Learning Paths",
    "nav.auditLog": "Audit Log",

    // Greetings
    "greeting.goodNight": "Good night",
    "greeting.goodMorning": "Good morning",
    "greeting.goodAfternoon": "Good afternoon",
    "greeting.goodEvening": "Good evening",

    // Dashboard
    "dashboard.welcome": "Welcome!",
    "dashboard.readyToStart": "Ready to start?",
    "dashboard.firstCourseWaiting": "Your first course is waiting. Click below to begin.",
    "dashboard.startFirstCourse": "Start first course",
    "dashboard.status": "Status",
    "dashboard.lessons": "Lessons",
    "dashboard.ofCompleted": "{total} completed",
    "dashboard.coursesCompleted": "Courses completed",
    "dashboard.ofCourses": "of {total} courses",
    "dashboard.totalProgress": "Total progress",
    "dashboard.percentCompleted": "percent completed",
    "dashboard.yourCourses": "Your courses",
    "dashboard.completedCourses": "Completed courses",
    "dashboard.completed": "Completed",
    "dashboard.resume": "Resume",

    // Badges
    "badges.title": "Badges",
    "badges.points": "points",
    "badges.earned": "Earned ✓",
    "badges.ofBadgesEarned": "{earned} of {total} badges earned",

    // Leaderboard
    "leaderboard.title": "Leaderboard",
    "leaderboard.youAre": "You are #{rank}",
    "leaderboard.you": "(you)",
    "leaderboard.lessons": "lessons",
    "leaderboard.quizzes": "quizzes",
    "leaderboard.courses": "courses",
    "leaderboard.anonymous": "Anonymous",

    // Onboarding
    "onboarding.complete": "Onboarding complete! 🎉",
    "onboarding.gettingStarted": "Getting Started",
    "onboarding.tasksCompleted": "{done} of {total} tasks completed",
    "onboarding.allSet": "✨ You're all set! Welcome to the team.",

    // Campaigns
    "campaigns.title": "MiniQuiz",
    "campaigns.mandatory": "Mandatory",
    "campaigns.overdue": "Overdue",
    "campaigns.dueIn": "Due {time}",
    "campaigns.progress": "Progress",
    "campaigns.completed": "Completed",
    "campaigns.completedReview": "Completed – Review",
    "campaigns.continue": "Continue",
    "campaigns.start": "Start",
    "campaigns.new": "NEW",
    "campaigns.noContent": "No content linked yet",
    "campaigns.active": "Active",
    "campaigns.archived": "Archived",

    // Top header
    "topHeader.profile": "Profile",
    "topHeader.settings": "Settings",
    "topHeader.signOut": "Sign Out",

    // FAQ
    "faq.title": "Frequently Asked Questions",
    "faq.description": "Find answers to common questions about Trainly.",
    "faq.q1": "What is Trainly?",
    "faq.a1": "Trainly is a simple and modern LMS that makes it easy to plan, deliver and track employee training. Everything is gathered in one clear platform.",
    "faq.q2": "What sets Trainly apart from other LMS systems?",
    "faq.a2": "Many LMS systems are heavy and complicated. Trainly is built to be simple, quick to get started with and easy to use – for both management and employees.",
    "faq.q3": "How quickly can we get started?",
    "faq.a3": "Most customers are up and running within a few days. We help you with setup, structure and courses – so you avoid long projects.",
    "faq.q4": "Can we assign training based on role and location?",
    "faq.a4": "Yes. You can automatically assign courses based on role, department or location. The right person gets the right training – without manual work.",
    "faq.q5": "How do we follow up on employees who haven't completed training?",
    "faq.a5": "The system sends automatic reminders and gives you an overview of who needs follow-up.",
    "faq.q6": "Does Trainly work on mobile?",
    "faq.a6": "Yes. Employees can complete training on mobile, tablet and PC.",
    "faq.q7": "Can we get a demo before deciding?",
    "faq.a7": "Yes. Book a no-obligation demo and we'll show you how Trainly can give you better control and safer employees.",

    // Gamification categories
    "gamification.general": "General",
    "gamification.lessons": "Lessons",
    "gamification.quizzes": "Quizzes",
    "gamification.coursesPoints": "Courses & Points",

    // Language
    "language.no": "Norsk",
    "language.en": "English",

    // Course Viewer
    "course.notFound": "Course not found",
    "course.goBack": "Go back",
    "course.locked": "Course is locked",
    "course.completeFirst": "You must complete the following courses first:",
    "course.backToCourses": "Back to my courses",
    "course.element": "element",
    "course.elements": "elements",
    "course.percentDone": "{percent}% completed",
    "course.continueCourse": "Continue course",
    "course.startCourse": "Start course",
    "course.courseContent": "Course content",
    "course.stepOf": "Step {current} of {total}",
    "course.step": "Step {current}",
    "course.noLessons": "No lessons in this course yet.",
    "course.congrats": "Congratulations! 🎉",
    "course.completedAll": "You have completed all {count} lessons in",
    "course.reviewCourse": "Review course",
    "course.nextCourseReady": "Next course ready for you:",
    "course.startNextCourse": "Start next course",
    "course.documents": "Documents",
    "course.download": "Download",
    "course.completedMark": "Completed!",
    "course.passQuiz": "Pass the quiz to continue",
    "course.next": "Next",
    "course.completeCourse": "Complete course",
    "course.previous": "Previous",

    // Settings
    "settings.title": "Settings",
    "settings.description": "Manage organization settings and preferences",
    "settings.companyInfo": "Company Information",
    "settings.companyInfoDesc": "Edit basic information about your organization",
    "settings.companyName": "Company Name",
    "settings.directLink": "Direct Link",
    "settings.directLinkHelp": "Organization URL path. Subdomains require separate DNS configuration.",
    "settings.welcomeMessage": "Welcome message for employees",
    "settings.welcomePlaceholder": "e.g. Welcome to our training portal! Here you'll find all courses.",
    "settings.welcomeHelp": "Shown on the dashboard for all employees",
    "settings.orgId": "Organization ID",
    "settings.saveChanges": "Save changes",
    "settings.saving": "Saving...",
    "settings.yourRole": "Your role",
    "settings.yourRoleDesc": "Your access in this organization",
    "settings.organization": "Organization",
    "settings.role": "Your role",
    "settings.branding": "Branding",
    "settings.brandingDesc": "Customize your organization's logo and theme colors",
    "settings.logo": "Logo",
    "settings.uploadLogo": "Upload logo",
    "settings.uploading": "Uploading...",
    "settings.removeLogo": "Remove",
    "settings.logoRecommend": "Recommended: 256x256px, max 5MB",
    "settings.themeColors": "Theme Colors",
    "settings.primaryColor": "Primary Color",
    "settings.secondaryColor": "Secondary Color",
    "settings.saveColors": "Save colors",
    "settings.savingColors": "Saving...",
    "settings.devMode": "Developer Mode",
    "settings.devModeDesc": "Switch view role to test different user experiences",
    "settings.devModeNote": "💡 **Note:** This changes your UI view for testing and affects sidebar navigation immediately. Database permissions remain based on your actual role.",
    "settings.roleChanged": "Role changed",
    "settings.viewingAs": "You are now viewing the app as a {role}.",
    "settings.success": "Success",
    "settings.error": "Error",
    "settings.logoUploaded": "Logo cropped and uploaded!",
    "settings.logoRemoved": "Logo removed.",
    "settings.colorsUpdated": "Theme colors saved and updated!",
    "settings.companyUpdated": "Company information updated!",
    "settings.orgStructureDesc": "Manage departments, job roles and venues",
    "settings.uploadImage": "Please upload an image file.",
    "settings.imageTooLarge": "Image must be smaller than 5MB.",
    "settings.cropLogo": "Crop Logo",

    // Users
    "users.title": "Users",
    "users.members": "{count} members",
    "users.addUser": "Add User",
    "users.add": "Add",
    "users.searchPlaceholder": "Search users or invitations...",
    "users.selected": "{count} selected",
    "users.assign": "Assign",
    "users.remove": "Remove",
    "users.cancel": "Cancel",
    "users.allLocations": "All Locations",
    "users.allRoles": "All Roles",
    "users.allDepartments": "All Departments",
    "users.clearFilters": "Clear Filters",
    "users.name": "Name",
    "users.roleName": "Role",
    "users.progress": "Progress",
    "users.onboarding": "Onboarding",
    "users.lastActive": "Last Active",
    "users.ascending": "Ascending",
    "users.descending": "Descending",
    "users.selectAll": "Select all ({count})",
    "users.noMembers": "No members found",
    "users.noCourses": "No courses",
    "users.unknownUser": "Unknown User",
    "users.neverLoggedIn": "Never logged in",
    "users.edit": "Edit",
    "users.protectedUser": "Protected User",
    "users.resendLogin": "Resend Login Info",
    "users.sendReminder": "Send Course Reminder",
    "users.removeManager": "Remove Manager",
    "users.makeManager": "Make Manager",

    // Profile
    "profile.title": "My Profile",
    "profile.description": "View and edit your profile information",
    "profile.picture": "Profile Picture",
    "profile.pictureDesc": "Upload an image that represents you",
    "profile.imageFormat": "JPG, PNG or GIF. Max 5MB.",
    "profile.personalInfo": "Personal Information",
    "profile.personalInfoDesc": "Update your contact information",
    "profile.fullName": "Full Name",
    "profile.namePlaceholder": "Enter your name",
    "profile.email": "Email",
    "profile.emailCantChange": "Email cannot be changed",
    "profile.phone": "Phone",
    "profile.organization": "Organization",
    "profile.saveChanges": "Save Changes",
    "profile.changePassword": "Change Password",
    "profile.changePasswordDesc": "Update your password for increased security",
    "profile.newPassword": "New Password",
    "profile.newPasswordPlaceholder": "Enter new password",
    "profile.confirmPassword": "Confirm New Password",
    "profile.confirmPasswordPlaceholder": "Confirm new password",
    "profile.updatePassword": "Change Password",
    "profile.profileUpdated": "Profile updated!",
    "profile.profileError": "Could not update profile",
    "profile.passwordUpdated": "Password updated!",
    "profile.passwordMismatch": "Passwords do not match",
    "profile.passwordTooShort": "Password must be at least 6 characters",
    "profile.selectImage": "Please select an image file",
    "profile.imageUnder5MB": "Image must be under 5MB",
    "profile.pictureUpdated": "Profile picture updated!",
    "profile.pictureError": "Could not upload image",
    "profile.cropPicture": "Crop Profile Picture",

    // Roles
    "role.superAdmin": "Super Admin",
    "role.superAdminDesc": "Full access to all organizations and features",
    "role.manager": "Manager",
    "role.managerDesc": "Manage users, courses, and content in this organization",
    "role.learner": "Learner",
    "role.learnerDesc": "Access assigned courses and track progress",
    "role.admin": "Admin",

    // Course Editor
    "editor.courseNotFound": "Course not found",
    "editor.goBack": "Go back",
    "editor.elements": "elements",
    "editor.saveChanges": "Save changes",
    "editor.saveAndFinish": "Save and finish",
    "editor.finish": "Finish",
    "editor.content": "Content",
    "editor.details": "Details",
    "editor.targeting": "Targeting",
    "editor.requirements": "Requirements",
    "editor.certificate": "Certificate",
    "editor.addElement": "Add element",
    "editor.addNewElement": "Add new element",
    "editor.name": "Name",
    "editor.namePlaceholder": "e.g. Introduction to food safety",
    "editor.videoUrl": "Video URL (optional)",
    "editor.videoPlaceholder": "e.g. https://youtube.com/watch?v=...",
    "editor.videoHelp": "YouTube, Vimeo or Loom",
    "editor.files": "Files (optional)",
    "editor.dropFiles": "Drag and drop files, or",
    "editor.clickToSelect": "click to select",
    "editor.fileTypes": "PDF, images, video, documents",
    "editor.adding": "Adding...",
    "editor.noElements": "No elements yet. Add your first element!",
    "editor.expandTip": "Expand an element to add quizzes and documents",
    "editor.quizTip": "You can add quizzes and more details afterwards",
    "editor.courseTitle": "Course title",
    "editor.courseTitlePlaceholder": "Course title",
    "editor.description": "Description",
    "editor.descriptionPlaceholder": "What will participants learn in this course?",
    "editor.coverImage": "Cover image",
    "editor.changeImage": "Change image",
    "editor.swap": "Swap",
    "editor.removeImg": "Remove",
    "editor.clickUploadCover": "Click to upload a cover image",
    "editor.coverFormat": "JPG, PNG or GIF. Max 5MB.",
    "editor.cropCover": "Crop cover image",
    "editor.selectImage": "Please select an image",
    "editor.imageUnder5MB": "Image must be under 5MB",
    "editor.courseUpdated": "Course updated!",
    "editor.courseUpdateError": "Could not update course",
    "editor.elementAdded": "Element added!",
    "editor.elementAddError": "Could not add element",
    "editor.elementDeleted": "Element deleted",
    "editor.elementUpdated": "Element updated",
    "editor.orderUpdated": "Order updated",
    "editor.orderError": "Could not update order",
    "editor.imageUploaded": "Image uploaded!",
    "editor.imageUploadError": "Could not upload image",
    "editor.wizardSkip": "Skip",
    "editor.wizardNext": "Next",
    "editor.wizardFinish": "Finish setup",
    "editor.wizardBack": "Back",
    "editor.wizardExitLabel": "Exit wizard",
    "editor.wizardStepLabel": "Course setup",
    "editor.wizardOptional": "Optional",
    "editor.wizardStepContent": "Add elements, videos and documents",
    "editor.wizardStepDetails": "Set title, description and cover image",
    "editor.wizardStepTargeting": "Choose who the course is assigned to",
    "editor.wizardStepRequirements": "Set prerequisite courses",
    "editor.wizardStepCertificate": "Configure certification on completion",

    // MyCourses
    "myCourses.title": "My Courses",
    "myCourses.subtitle": "Track your learning progress",
    "myCourses.total": "Total",
    "myCourses.inProgress": "In Progress",
    "myCourses.completed": "Completed",
    "myCourses.searchPlaceholder": "Search courses...",
    "myCourses.all": "All",
    "myCourses.noMatch": "No courses match your filters",
    "myCourses.noCoursesYet": "No courses assigned yet",
    "myCourses.lessons": "lessons",
    "myCourses.locked": "Locked",
    "myCourses.requires": "Requires:",
    "myCourses.overallProgress": "Overall progress",
    "myCourses.coursesCompleted": "courses completed",
    "myCourses.continue": "Continue",

    // Certificates
    "certificates.title": "My Certificates",
    "certificates.description": "Overview of your earned certificates",
    "certificates.none": "No certificates yet",
    "certificates.noneDesc": "Complete courses to receive certificates",
    "certificates.valid": "Valid",
    "certificates.expired": "Expired",
    "certificates.expiringSoon": "Expiring soon",
    "certificates.revoked": "Revoked",
    "certificates.issued": "Issued",
    "certificates.expires": "Expires",
    "certificates.noExpiry": "No expiry date",
    "certificates.view": "View certificate",
    "certificates.heading": "Certificate",
    "certificates.subheading": "Certificate of Completion",
    "certificates.awardedTo": "Awarded to",
    "certificates.forCompletion": "for completing the course",
    "certificates.validUntil": "Valid until",
    "certificates.certNumber": "Certificate No.",
    "certificates.close": "Close",
    "certificates.download": "Download",

    // MiniQuiz / Campaigns
    "miniquiz.title": "MiniQuiz",
    "miniquiz.subtitle": "Quick quizzes and content for your team",
    "miniquiz.new": "New MiniQuiz",
    "miniquiz.none": "No MiniQuiz yet",
    "miniquiz.noneDesc": "Create a MiniQuiz to share quick quizzes and content with your team.",
    "miniquiz.edit": "Edit MiniQuiz",
    "miniquiz.quizName": "Quiz name",
    "miniquiz.quizNamePlaceholder": "e.g. New menu changes",
    "miniquiz.textContent": "Text / content",
    "miniquiz.textPlaceholder": "Write the information employees should read before the quiz...",
    "miniquiz.textHelp": "This is shown as a lesson before the quiz",
    "miniquiz.questions": "Questions",
    "miniquiz.addQuestion": "Add question",
    "miniquiz.question": "Question",
    "miniquiz.writeQuestion": "Write the question...",
    "miniquiz.markCorrect": "Mark as correct answer",
    "miniquiz.optionPlaceholder": "Answer option",
    "miniquiz.addOption": "+ Add answer option",
    "miniquiz.settingsTitle": "Settings",
    "miniquiz.deadline": "Deadline (optional)",
    "miniquiz.mandatory": "Mandatory",
    "miniquiz.cancel": "Cancel",
    "miniquiz.saving": "Saving...",
    "miniquiz.save": "Save",
    "miniquiz.create": "Create MiniQuiz",
    "miniquiz.updated": "MiniQuiz updated",
    "miniquiz.created": "MiniQuiz created!",
    "miniquiz.error": "Something went wrong",
    "miniquiz.reactivated": "MiniQuiz reactivated",
    "miniquiz.archived": "MiniQuiz archived",
    "miniquiz.statusError": "Could not update status",
    "miniquiz.deleted": "MiniQuiz deleted",
    "miniquiz.deleteError": "Could not delete",
    "miniquiz.course": "Course",
    "miniquiz.deadlineLabel": "Deadline",

    // Manager Dashboard
    "manager.goodMorning": "Good morning",
    "manager.goodAfternoon": "Good afternoon",
    "manager.goodEvening": "Good evening",
    "manager.viewProgress": "View Progress",
    "manager.inviteTeam": "Invite Team",
    "manager.team": "Team",
    "manager.courses": "Courses",
    "manager.progress": "Progress",
    "manager.completed": "Completed",
    "manager.reportSent": "Report sent to your email!",
    "manager.reportError": "Could not send report. Please check email configuration.",
    "manager.teamMilestone": "Team milestone!",
    "manager.coursesCompletedTotal": "{count} courses completed in total",

    // Activity Feed
    "activity.recentActivity": "Recent Activity",
    "activity.viewAll": "View All",
    "activity.noActivity": "No activity yet",
    "activity.activityWillAppear": "Activity will appear here when the team starts",
    "activity.completed": "completed",
    "activity.recently": "recently",

    // Course Stats (Manager)
    "courseStats.title": "Course Statistics",
    "courseStats.analytics": "Analytics",
    "courseStats.noCourses": "No courses created",
    "courseStats.createToSee": "Create courses to see statistics",
    "courseStats.createCourse": "Create Course",
    "courseStats.enrolled": "enrolled",
    "courseStats.lessons": "lessons",

    // Manager Leaderboard
    "managerLeaderboard.title": "Leaderboard",
    "managerLeaderboard.noData": "No data yet",
    "managerLeaderboard.updatesWhen": "The leaderboard updates when the team completes lessons",
    "managerLeaderboard.points": "points",
    "managerLeaderboard.lessons": "lessons",

    // Team Goals
    "goals.title": "Team Goals",
    "goals.completionRate": "100% completion rate",
    "goals.everyoneTakesQuizzes": "Everyone takes quizzes",
    "goals.weeklyActivity": "Weekly activity",

    // Quick Actions
    "quickActions.title": "Quick Actions",
    "quickActions.inviteTeamMember": "Invite team member",
    "quickActions.createNewCourse": "Create new course",
    "quickActions.settings": "Settings",
    "quickActions.sendReport": "Send report to me",
    "quickActions.sending": "Sending...",

    // Learner Progress
    "learnerProgress.title": "Progress",
    "learnerProgress.description": "Track individual learner completion rates and activity",
    "learnerProgress.totalLearners": "Total Learners",
    "learnerProgress.avgCompletion": "Avg Completion",
    "learnerProgress.inProgress": "In Progress",
    "learnerProgress.completedAll": "Completed All",
    "learnerProgress.searchPlaceholder": "Search by name...",
    "learnerProgress.all": "All",
    "learnerProgress.active": "In Progress",
    "learnerProgress.completed": "Completed",
    "learnerProgress.notStarted": "Not Started",
    "learnerProgress.allLocations": "All Locations",
    "learnerProgress.allRoles": "All Roles",
    "learnerProgress.noLearners": "No learners found",
    "learnerProgress.lastActive": "Last active {time}",
    "learnerProgress.noActivityYet": "No activity yet",
    "learnerProgress.coursesLabel": "Courses",
    "learnerProgress.lessonsLabel": "Lessons",

    // Course Analytics
    "analytics.title": "Course Insight",
    "analytics.description": "Track lesson engagement, drop-off and quiz results",
    "analytics.allCourses": "All Courses",
    "analytics.allLocations": "All Locations",
    "analytics.allRoles": "All Roles",
    "analytics.allDepartments": "All Departments",
    "analytics.export": "Export",
    "analytics.exported": "Data exported as CSV",
    "analytics.noCoursesToAnalyze": "No courses to analyze yet",
    "analytics.courseOverview": "Course Overview",
    "analytics.course": "Course",
    "analytics.lessons": "Lessons",
    "analytics.lessonsAndEnrolled": "{lessons} lessons • {enrolled} enrolled",
    "analytics.completedPercent": "Completed",
    "analytics.passed": "passed",
    "analytics.failed": "failed",
    "analytics.avgTime": "Avg Time",
    "analytics.lessonBreakdown": "Lesson-by-lesson breakdown",
    "analytics.dropOff": "{percent}% drop-off",
    "analytics.ofCompleted": "{completed} of {total} completed",
    "analytics.overview": "Overview",
    "analytics.performance": "Performance",
    "analytics.totalUsers": "Total Users",
    "analytics.activeUsers": "Active Users (MAU)",
    "analytics.coursesCompleted": "Courses Completed",
    "analytics.quizPassRate": "Quiz Pass Rate",
    "analytics.enrollmentRate": "Enrollment Rate",
    "analytics.completionRate": "Completion Rate",
    "analytics.avgQuizScore": "Avg Quiz Score",
    "analytics.onboardingTime": "Estimated Course Time",
    "analytics.onboardingDays": "Time to Complete",
    "analytics.mobileUsage": "Mobile Usage",
    "analytics.trainingGaps": "Training Gaps",
    "analytics.lowestCompletion": "Lowest Completion Rate",
    "analytics.highestQuizFailure": "Highest Quiz Failure Rate",
    "analytics.problemLessons": "Most Challenging Lessons",
    "analytics.completedOf": "completed",
    "analytics.noQuizData": "No quiz data yet",
    "analytics.failRate": "fail rate",
    "analytics.noData": "No data yet",
    "analytics.completionLower": "completed",

    // Venue Comparison
    "venue.locationComparison": "Location Comparison",
    "venue.location": "Location",
    "venue.users": "Users",
    "venue.enrollments": "Enrollments",
    "venue.completions": "Completions",
    "venue.completionRate": "Completion Rate",
    "venue.quizPassRate": "Quiz Pass Rate",
    "venue.avgScore": "Avg Score",

    // User Detail
    "userDetail.title": "User details",
    "userDetail.userNotFound": "User not found",
    "userDetail.notFoundDesc": "This user was not found in this organization.",
    "userDetail.goBack": "Go back",
    "userDetail.assignCourse": "Assign course",
    "userDetail.issueCert": "Issue certificate",
    "userDetail.edit": "Edit",
    "userDetail.back": "Back",
    "userDetail.description": "User details and training status",
    "userDetail.departments": "Departments",
    "userDetail.jobRoles": "Job Roles",
    "userDetail.locations": "Locations",
    "userDetail.lastLogin": "Last login",
    "userDetail.neverLoggedIn": "Never logged in",
    "userDetail.memberSince": "Member since",
    "userDetail.coursesAssigned": "Courses assigned",
    "userDetail.overallCompletion": "Overall completion",
    "userDetail.certificates": "Certificates",
    "userDetail.badges": "Badges",
    "userDetail.courseProgress": "Course Progress",
    "userDetail.noCourses": "No courses assigned",
    "userDetail.unknownCourse": "Unknown course",
    "userDetail.lessonsCount": "lessons",
    "userDetail.lastActiveTime": "Last active",
    "userDetail.completedStatus": "Completed",
    "userDetail.inProgressStatus": "In progress",
    "userDetail.notStartedStatus": "Not started",
    "userDetail.quizResults": "Quiz results",
    "userDetail.noActivity": "No activity recorded",
    "userDetail.activityTimeline": "Activity Timeline",
    "userDetail.completedLesson": "completed",

    // Manage Courses
    "manageCourses.title": "Manage Courses",
    "manageCourses.createCourse": "Create Course",
    "manageCourses.createNew": "Create New Course",
    "manageCourses.titleLabel": "Title",
    "manageCourses.descriptionLabel": "Description",
    "manageCourses.coverImage": "Cover Image (optional)",
    "manageCourses.dragDrop": "Drag & drop or click to upload",
    "manageCourses.creating": "Creating...",
    "manageCourses.editCourse": "Edit Course",
    "manageCourses.assignCourse": "Assign Course",
    "manageCourses.searchPlaceholder": "Search courses...",
    "manageCourses.noCourses": "No courses created yet",
    "manageCourses.createFirst": "Create your first course to get started",
    "manageCourses.deleteCourse": "Delete Course",
    "manageCourses.deleteConfirm": "Are you sure you want to delete this course? This cannot be undone.",
    "manageCourses.typeToConfirm": "Type the course title to confirm",
    "manageCourses.cancelDelete": "Cancel",
    "manageCourses.deletePermanent": "Delete permanently",
    "manageCourses.sendReminder": "Send Reminder",
    "manageCourses.reminderSent": "Reminder sent to {count} learner(s)",
    "manageCourses.reminderError": "Could not send reminders. Please try again.",
    "manageCourses.courseTitlePlaceholder": "Course title",
    "manageCourses.courseDescPlaceholder": "Course description",
    "manageCourses.couldNotCreate": "Could not create course",
    "manageCourses.cropCover": "Crop Cover Image",
    "manageCourses.lessonsCount": "lessons",
    "manageCourses.enrolledCount": "enrolled",
    "manageCourses.noDescription": "No description",
    "manageCourses.assignUsers": "Assign Users",
    "manageCourses.sending": "Sending...",
    "manageCourses.sharedLibrary": "Shared Library",
    "manageCourses.sharedLibraryDesc": "These courses can be assigned to any organization",
    "manageCourses.assignToOrg": "Assign to this org",
    "manageCourses.courseCreated": "Course created!",
    "manageCourses.courseDeleted": "Course deleted",
    "manageCourses.courseAssigned": "Course assigned to organization",
    "manageCourses.everyoneCompleted": "Everyone has completed this course!",

    // Time periods
    "timePeriod.allTime": "All Time",
    "timePeriod.today": "Today",
    "timePeriod.yesterday": "Yesterday",
    "timePeriod.thisWeek": "This Week",
    "timePeriod.thisMonth": "This Month",
    "timePeriod.lastMonth": "Last Month",
    "timePeriod.last3Months": "Last 3 Months",
    "timePeriod.lastYear": "Last Year",
    "timePeriod.customRange": "Custom Range",
    "timePeriod.from": "From",
    "timePeriod.to": "To",

    // User Detail extra
    "userDetail.joinedOrg": "Joined the organization",
    "userDetail.assignedCourse": "Assigned course: {name}",
    "userDetail.completedCourse": "Completed course: {name}",
    "userDetail.completedLessonName": "Completed lesson: {name}",
    "userDetail.quizName": "Quiz: {name}",
    "userDetail.quizScore": "Score: {score}% — {result}",
    "userDetail.passed": "Passed",
    "userDetail.failed": "Failed",
    "userDetail.certIssued": "Certificate issued: {name}",
    "userDetail.badgeEarned": "Badge earned: {name}",
    "userDetail.issuedLabel": "Issued",
    "userDetail.expiresLabel": "Expires",
    "userDetail.documentsReview": "Documents for review",
    "userDetail.confirmedRead": "Confirmed read",
    "userDetail.openedNotConfirmed": "Opened – not confirmed",
    "userDetail.notOpened": "Not opened",
    "userDetail.readStatus": "Read",
    "userDetail.openedStatus": "Opened",
    "userDetail.unreadStatus": "Unread",
    "userDetail.unknownUser": "Unknown user",
    "userDetail.progressLabel": "Progress",

    // Landing page
    "landing.nav.problems": "Problems",
    "landing.nav.features": "Features",
    "landing.nav.pricing": "Pricing",
    "landing.nav.caseStudy": "What do our customers say?",
    "landing.nav.guide": "Guide",
    "landing.nav.login": "Log in",
    "landing.nav.bookDemo": "Book a demo",
    "landing.hero.badge": "Norwegian digital training platform",
    "landing.hero.title": "Do you have control over your training?",
    "landing.hero.subtitle": "Trainly makes employee training super easy, automatic and clear. Less time spent, faster results and full control!",
    "landing.hero.cta": "Book a demo",
    "landing.hero.cardAdmin": "Create courses & quizzes with AI",
    "landing.hero.cardControl": "Full training insight",
    "landing.hero.cardSafety": "Safer employees who perform better",
    "landing.socialProof": "We work with companies that care about results:",
    "landing.benefits.badge": "Benefits of Trainly LMS",
    "landing.benefits.title": "Full control over training.",
    "landing.benefits.subtitle": "With Trainly, all employees get consistent, quality-assured training regardless of location or who's on duty.",
    "landing.benefits.sameTraining": "Save time and money on training",
    "landing.benefits.sameTrainingDesc": "Stop spending on manual follow-ups and time-consuming tasks. With structured digital training, you save time, resources, and unnecessary costs.",
    "landing.benefits.tag1": "Efficiency",
    "landing.benefits.tag2": "Cost-saving",
    "landing.benefits.tag3": "Automated",
    "landing.benefits.tag4": "Results",
    "landing.benefits.documented": "Safer employees, better results",
    "landing.benefits.documentedDesc": "Well-trained employees work more independently and make fewer mistakes. This means higher quality, better customer experiences, and more professional operations.",
    "landing.benefits.overview": "New employees operational in hours, not weeks",
    "landing.benefits.overviewDesc": "Give new hires a structured and clear start from day one. They arrive prepared, feel more confident, and contribute to value creation faster.",
    "landing.benefits.saferResults": "Real-time insights with dashboards and reports",
    "landing.benefits.saferResultsDesc": "Get a complete overview of completion, certificates, and competencies across the entire organization. Make decisions based on data, not assumptions.",
    "landing.benefits.fewerErrors": "Full overview of the entire organization",
    "landing.benefits.higherQuality": "Compare department to department",
    "landing.benefits.betterSatisfaction": "Engagement and completion over time",
    "landing.benefits.lowerTurnover": "See employees who stand out!",
    "landing.beforeAfter.title1": "Go from",
    "landing.beforeAfter.chaos": "chaos",
    "landing.beforeAfter.to": "to",
    "landing.beforeAfter.control": "control",
    "landing.beforeAfter.subtitle": "See the difference between traditional training and what you get with Trainly.",
    "landing.beforeAfter.tryFree": "Try for free",
    "landing.beforeAfter.traditional": "Traditional training",
    "landing.before.1": "Training scattered across email, binders and word of mouth",
    "landing.before.2": "Different training depending on who's teaching",
    "landing.before.3": "Expensive physical courses and manual follow-up",
    "landing.before.4": "New employees take weeks to become productive",
    "landing.before.5": "No overview of completed training",
    "landing.before.6": "Only available at the office during work hours",
    "landing.before.7": "Routine changes must be communicated manually",
    "landing.before.8": "Uneven skill levels lead to errors and risk",
    "landing.after.1": "Everything in one place — courses, routines and tests",
    "landing.after.2": "Same training for everyone, regardless of place or time",
    "landing.after.3": "Save time and money on training",
    "landing.after.4": "New employees productive in days, not weeks",
    "landing.after.5": "Full traceability for HSE and internal controls",
    "landing.after.6": "Available on mobile, tablet and PC",
    "landing.after.7": "Update once — everyone gets the latest version",
    "landing.after.8": "Safer employees, fewer errors and better operations",
     "landing.pricing.badge": "See our simple pricing",
     "landing.pricing.title": "Simple and transparent pricing",
    "landing.pricing.subtitle": "Choose the plan that fits your business. No commitment — all plans include a 14-day free trial.",
    "landing.pricing.freeOnboarding": "Free onboarding included!",
    "landing.pricing.noCommitment": "No commitment",
    "landing.pricing.mostPopular": "Most popular",
    "landing.pricing.contactUs": "Contact us",
    "landing.pricing.start": "Start",
    "landing.pricing.startDesc": "For smaller businesses that want structured and simple training.",
    "landing.pricing.startSub": "",
    "landing.pricing.startCapacity": "Up to 50 employees",
    "landing.pricing.startFooter": "Perfect for individual departments and smaller teams.",
    "landing.pricing.startCta": "Get started",
    "landing.pricing.growth": "Growth",
    "landing.pricing.growthDesc": "For businesses with multiple departments needing more insight and control.",
    "landing.pricing.growthSub": "",
    "landing.pricing.growthCapacity": "Up to 200 employees",
    "landing.pricing.growthFooter": "Perfect for chains and growing businesses.",
    "landing.pricing.enterprise": "Enterprise",
    "landing.pricing.enterpriseDesc": "For larger organizations and chains with specific needs.",
    "landing.pricing.enterpriseSub": "",
    "landing.pricing.enterprisePrice": "Contact us",
    "landing.pricing.enterpriseFooter": "Contact us for a custom quote.",
    "landing.pricing.enterpriseCta": "Contact sales",
    "landing.pricing.f.unlimited": "Unlimited courses",
    "landing.pricing.f.courseBuilder": "Course builder with video, text and documents",
    "landing.pricing.f.quizzes": "Tests and quizzes with automatic grading",
    "landing.pricing.f.roleAssign": "Role-based training assignment",
    "landing.pricing.f.tracking": "Completion tracking",
    "landing.pricing.f.certs": "Certificates and reminders",
    "landing.pricing.f.mobile": "Mobile and desktop",
    "landing.pricing.f.emailSupport": "Email support",
    "landing.pricing.f.allStart": "Everything in Start, plus:",
    "landing.pricing.f.deptLocation": "Department and location structure",
    "landing.pricing.f.advInsight": "Advanced insights and reports",
    "landing.pricing.f.autoFollow": "Automatic follow-up and alerts",
    "landing.pricing.f.multiAdmin": "Multiple admin roles",
    "landing.pricing.f.prioSupport": "Priority support",
    "landing.pricing.f.integrations": "Integration options",
    "landing.pricing.f.allGrowth": "Everything in Growth, plus:",
    "landing.pricing.f.custom": "Custom setup",
    "landing.pricing.f.ownOnboard": "Dedicated onboarding advisor",
    "landing.pricing.f.integrationsEnt": "Integrations (HR, SSO, API)",
    "landing.pricing.f.advReporting": "Advanced reporting and export",
    "landing.pricing.f.dedicated": "Dedicated customer success contact",
    "landing.pricing.f.aiCourses": "AI-generated courses and quizzes",
    "landing.pricing.f.whiteLabel": "Custom branding and colors",
    "landing.pricing.f.multiOrg": "Multiple locations / companies",
    "landing.pricing.f.sla": "SLA and priority response time",
    "landing.faq.title": "Frequently asked questions about Trainly",
    "landing.faq.subtitle": "Everything you need to know about our employee training platform",
    "landing.faq.q1": "What is an LMS (Learning Management System)?",
    "landing.faq.a1": "An LMS, or Learning Management System, is a digital platform that lets companies create, distribute and track employee training. Instead of physical courses, binders and spreadsheets, an LMS gathers everything in one place – courses, quizzes, certifications and reporting. Trainly is an LMS designed specifically for small and medium-sized businesses, making it easy to get started with digital training without technical expertise.",
    "landing.faq.q2": "What is Trainly, and who is it for?",
    "landing.faq.a2": "Trainly is an employee training platform (LMS) designed for companies with 10–300 employees. The platform works for all industries but is especially popular among restaurants, chain businesses, transport, construction and service companies. Trainly makes it easy to create courses, assign training based on role and location, and track progress – all in one solution.",
    "landing.faq.q3": "What sets Trainly apart from other LMS systems?",
    "landing.faq.a3": "Many LMS systems are built for large enterprises and require long implementation times and technical expertise. Trainly is built to be simple and user-friendly from day one. You can create courses with AI in minutes, automatically assign training based on role and department, and get full visibility into progress without complex setup. Plus, there's no commitment period.",
    "landing.faq.q4": "How quickly can we get started with Trainly?",
    "landing.faq.a4": "Most companies are up and running within a few days. We help you with setup, course structure and content. With the AI tool, you can create professional courses and quizzes in minutes by entering a procedure or uploading a document. You don't need any technical background to use Trainly.",
    "landing.faq.q5": "What does Trainly cost?",
    "landing.faq.a5": "Trainly has flexible plans that fit different company sizes. There's no startup cost and no commitment period – you pay per user per month. Contact us for a no-obligation quote based on your number of employees and needs, or check our pricing page for more information.",
    "landing.faq.q6": "Can Trainly be used for onboarding new employees?",
    "landing.faq.a6": "Yes, onboarding is one of the most used features in Trainly. New employees automatically get the right training path from day one, based on role, department and location. This ensures everyone gets the same quality training regardless of who's on shift. Companies using Trainly for onboarding experience faster productivity and more confident new hires.",
    "landing.faq.q7": "Does Trainly work for restaurants and chain businesses?",
    "landing.faq.a7": "Yes, Trainly is ideal for restaurants, cafes and chain businesses. You can create standardized courses for food service, hygiene, health & safety and customer service, and assign them automatically to employees across locations. Managers get visibility into who has completed training at each location, and employees can take courses on their phone whenever it suits them.",
    "landing.faq.q8": "Can employees complete training on mobile?",
    "landing.faq.a8": "Yes, Trainly works on mobile, tablet and PC. Employees can complete courses, take quizzes and read documents from their preferred device. This is especially useful for employees who work in the field, on the floor or in shifts – they don't need to sit at a PC to complete their training.",
    "landing.faq.q9": "How does the AI course builder in Trainly work?",
    "landing.faq.a9": "Trainly has a built-in AI that can generate complete courses and quizzes based on text or documents you upload. Enter a procedure, a topic or upload a PDF, and the AI creates a structured course with lessons and questions in minutes. You can then edit and customize the content. This saves significant time compared to creating everything from scratch.",
    "landing.faq.q10": "Can we assign courses based on role, department and location?",
    "landing.faq.a10": "Yes. Trainly lets you set up automatic course assignment based on roles, departments and locations. A server in one city and a chef in another can automatically get different training relevant to their position. When a new employee is added, they automatically get the right courses without manual work.",
    "landing.faq.q11": "How does Trainly help with health & safety and compliance?",
    "landing.faq.a11": "Trainly makes it easy to document that all employees have received required training in health & safety, internal control and industry requirements. Courses can be set as mandatory with passing score requirements. Certificates are tracked automatically with alerts before expiry. During audits, you can export full documentation with one click – who completed what, when and with what result.",
    "landing.faq.q12": "How do we follow up on employees who haven't completed training?",
    "landing.faq.a12": "Trainly sends automatic reminders to employees who haven't completed mandatory training. Managers get an overview in the dashboard of who is missing courses, and can send extra reminders with one click. You don't need to track this manually.",
    "landing.faq.q13": "Is there a commitment period on Trainly?",
    "landing.faq.a13": "No, there's no commitment period. You can cancel your subscription at any time. We believe the product should speak for itself – not long contracts. Most customers stay because they experience real value from the first week.",
    "landing.faq.q14": "Can we get a free demo of Trainly?",
    "landing.faq.a14": "Yes! You can book a free, no-obligation demo where we show you how Trainly works for your type of business. We look at your needs and show concretely how the platform can help you with training, onboarding and employee follow-up.",
    "landing.faq.q15": "What's the difference between Trainly and free LMS solutions?",
    "landing.faq.a15": "Free LMS solutions often require technical expertise for setup, lack local language support, and have limited functionality for tracking and reporting. Trainly is pre-configured, has a localized interface and support, AI course building, automatic assignment and complete reporting – without needing a technical background. You save time and get a solution that actually gets used.",
    "landing.faq.q16": "Can Trainly be used for training across multiple locations?",
    "landing.faq.a16": "Yes, Trainly is built for companies with multiple branches and locations. You can compare completion rates between locations, assign location-specific training, and ensure all employees get the training they need – regardless of where they work. Managers at each location can follow up their team, while headquarters gets full visibility.",
    "landing.testimonials.label": "Our Customers",
    "landing.testimonials.content": "After implementing Trainly, we've reduced our costs, but most importantly: training has reached a whole new level. Employees are more confident in their roles, and we have full visibility and control — without extra administration.",
    "landing.testimonials.cta": "Read how CITY AS went from binders to full digital control",
    "landing.guide.title": "Get tips for smarter training",
    "landing.guide.subtitle": "Our guide shows you step by step how to set up Trainly, create courses and invite employees. Everything you need for a quick and safe start.",
    "landing.guide.seeAll": "See all our guides",
    "landing.guide.post1": "What is an LMS — and why do Norwegian businesses need it now?",
    "landing.guide.post2": "7 signs your business needs an employee training system",
    "landing.guide.post3": "From Excel and binders to full control: Digitalize your training",
    "landing.footer.description": "Modern training platform for businesses. We make internal training simple, trackable and engaging.",
    "landing.footer.navigation": "Navigation",
    "landing.footer.pages": "Pages",
    "landing.footer.contact": "Contact",
    "landing.footer.bookMeeting": "Book a meeting",
    "landing.footer.privacy": "Privacy",
    "landing.footer.madeWith": "Made with ❤️ by",
    "landing.footer.light": "Light",
    "landing.footer.dark": "Dark",
    "landing.nav.home": "Home",
    "landing.nav.contact": "Contact",
    "landing.mockup.title": "is available wherever you are.",
    "landing.mockup.desc": "Employees complete courses, take quizzes and keep certifications updated directly from mobile or PC. Managers get full insight and control, right where they are.",
    "landing.mockup.cta": "Get started",
    "landing.mockup.features": "See all features",
    "landing.mockup.greeting": "Hi, Sara 👋",
    "landing.mockup.myCourses": "My courses",
    "landing.mockup.totalProgress": "Total progress",
    "landing.mockup.coursesOf": "5 of 7 courses completed",
    "landing.mockup.ongoing": "Ongoing",
    "landing.mockup.newCert": "New certificate!",
    "landing.mockup.certDone": "Fire safety completed",
    "landing.mockup.courseCompleted": "Course completed!",
    "landing.mockup.courseCompletedSub": "Fire safety ✓",
    "landing.mockup.quizPassed": "Quiz passed",
    "landing.mockup.quizScore": "Score: 95%",
    "landing.mockup.tabCourses": "Courses",
    "landing.mockup.tabCerts": "Certs",
    "landing.mockup.tabGoals": "Goals",
    "landing.mockup.tabProfile": "Profile",
    "landing.problems.title": "Challenges we",
    "landing.problems.titleItalic": "solve",
    "landing.problems.subtitle": "With Trainly, training becomes a strength, not a challenge. Easy to use, easy to follow up, easy to document.",
    "landing.problems.commonChallenges": "Common challenges",
    "landing.problems.tryFree": "Try for free",
    "landing.problems.p1": "It takes too long to create courses and quizzes",
    "landing.problems.p2": "Training is scattered across email and binders",
    "landing.problems.p3": "Quality varies depending on who's training",
    "landing.problems.p4": "New hires take too long to become productive",
    "landing.problems.p5": "No unified overview of completed training",
    "landing.problems.p6": "Lack of insight into employee competency levels",
    "landing.problems.p7": "High costs for physical courses and gatherings",
    "landing.problems.p8": "Too much manual follow-up from managers",
    "landing.problems.p9": "Routine changes are communicated manually",
    "landing.problems.p10": "Uneven competency levels lead to errors and risk",
    "landing.problems.p11": "Hard to document for inspections",
    "landing.problems.p12": "Growth makes training harder to manage",
    "landing.problems.p13": "Knowledge disappears when employees leave",
    "landing.problems.s1": "Create courses and quizzes in minutes with AI",
    "landing.problems.s2": "Everything in one place! Courses, onboarding and tests",
    "landing.problems.s3": "Equal training for all, regardless of language, location or time",
    "landing.problems.s4": "New employees productive in hours, not weeks",
    "landing.problems.s5": "Full traceability for HSE and internal controls",
    "landing.problems.s6": "Real-time insights with dashboards and reports",
    "landing.problems.s7": "Save time and money on training",
    "landing.problems.s8": "Automated assignment and reminders",
    "landing.problems.s9": "Update once, everyone gets the latest version",
    "landing.problems.s10": "Safer employees, fewer errors and better operations",
    "landing.problems.s11": "Documentation always ready for inspections",
    "landing.problems.s12": "Scales with your business, from 5 to 5000+ employees",
    "landing.problems.s13": "Knowledge preserved in the system",
    "landing.testimonials.title": "Results that matter",
    "landing.testimonials.readCase": "Read the full case",
    "landing.testimonials.roleCity": "CEO, CITY AS",
    "landing.testimonials.roleIsdalen": "CEO, Isdalen Green Transport",
    "landing.testimonials.quoteCity": "With Trainly we have structured and digitized our training. Employees are more confident, independent and deliver higher quality every day. We notice that employees feel well prepared, which makes them even better colleagues.",
    "landing.testimonials.quoteIsdalen": "Trainly has given us full insight and control over training. All courses were created quickly and easily using the built-in AI feature, and we now have documentation and status available at all times. It saves us time and gives us confidence in operations.",
    "landing.contact.title": "Contact us",
    "landing.contact.subtitle": "Have questions or want to book a demo? Fill in the form and we'll get back to you within 24 hours.",
    "landing.contact.email": "Email",
    "landing.contact.phone": "Phone",
    "landing.contact.bookDemo": "Book a demo with us",
    "landing.contact.name": "Name",
    "landing.contact.namePlaceholder": "Your name",
    "landing.contact.emailPlaceholder": "your@email.com",
    "landing.contact.company": "Company",
    "landing.contact.companyPlaceholder": "Company name",
    "landing.contact.message": "Message",
    "landing.contact.messagePlaceholder": "Tell us about your needs...",
    "landing.contact.success": "✅ Thank you for your inquiry! We'll get back to you within 24 hours.",
    "landing.contact.sending": "Sending...",
    "landing.contact.send": "Send message",
    "landing.funksjoner.badge": "AI-powered training",
    "landing.funksjoner.title": "A complete training system made simple",
    "landing.funksjoner.subtitle": "Scales with your business. Intuitive from the first click. Create courses, quizzes and structure without technical skills.",
    "landing.funksjoner.ctaTitle": "Ready to get started?",
    "landing.funksjoner.ctaSubtitle": "Try free for 14 days — no credit card required.",
    "landing.funksjoner.ctaButton": "Get started",
    "landing.funksjoner.ctaPricing": "See pricing",
    "landing.funksjoner.f1.title": "Courses, quizzes and knowledge tests",
    "landing.funksjoner.f1.desc": "Combine video, text, PDF and images with quizzes, tests and checklists. AI can generate questions for you automatically.",
    "landing.funksjoner.f2.title": "AI that saves you time",
    "landing.funksjoner.f2.desc": "Create courses from existing text, auto-generate quizzes and simplify content. Save hours every week on manual follow-up.",
    "landing.funksjoner.f3.title": "Assign by role, department and location",
    "landing.funksjoner.f3.desc": "Employees with multiple roles get the right training automatically. Especially useful for chains with many locations.",
    "landing.funksjoner.f4.title": "Certifications and expiry alerts",
    "landing.funksjoner.f4.desc": "Certificates on course completion, expiry dates and automatic alerts. 100% compliance control for audits and inspections.",
    "landing.funksjoner.f5.title": "Insights, analytics and progress",
    "landing.funksjoner.f5.desc": "Track who has completed, compare locations and departments, and identify competency gaps — all from one real-time dashboard.",
    "landing.funksjoner.f6.title": "Reminders, campaigns and follow-up",
    "landing.funksjoner.f6.desc": "Automatic alerts for incomplete training and expiring certifications. Send targeted campaigns to specific groups with deadlines.",
    "landing.funksjoner.f7.title": "Easy to use — quick setup",
    "landing.funksjoner.f7.desc": "Clean interface employees understand right away. We help you get started quickly without frustration.",
    "landing.funksjoner.f8.title": "Onboarding new employees",
    "landing.funksjoner.f8.desc": "Structured checklists and documents for new hires. Ensure everyone gets the same great start — regardless of location.",
    "landing.funksjoner.f9.title": "Custom subdomain for your organization",
    "landing.funksjoner.f9.desc": "Give employees a simple login via your own subdomain. Your brand, your setup, your colors.",
    "landing.funksjoner.f10.title": "Gamification and motivation",
    "landing.funksjoner.f10.desc": "Badges, leaderboards and progress tracking motivate employees to complete training and stay up to date.",
    "landing.funksjoner.f11.title": "Course library and reuse",
    "landing.funksjoner.f11.desc": "Share courses across locations and organizations. Build a library of reusable content.",
    "landing.funksjoner.f12.title": "Multi-location and chain management",
    "landing.funksjoner.f12.desc": "Manage training across many locations from one place. Perfect for restaurant chains, stores and service businesses.",
    "landing.funksjoner.f13.title": "Role-based access control",
    "landing.funksjoner.f13.desc": "Admin, manager and employee see only what they need. Delegate responsibility without losing control.",
    "landing.funksjoner.f14.title": "32+ languages, built in Norway",
    "landing.funksjoner.f14.desc": "Available in over 32 languages with Norwegian support. Developed and hosted in Norway for secure data storage and fast help.",
    "landing.case.badge": "Case Study",
    "landing.case.title": "Real results from real businesses",
    "landing.case.subtitle": "See how Trainly creates measurable results for businesses.",
    "landing.case.challenge": "The Challenge",
    "landing.case.solution": "The Solution",
    "landing.case.results": "Results",
    "landing.case.city.tag": "Full digital control",
    "landing.case.city.title": "From binders and PDFs to full digital control",
    "landing.case.city.desc": "CITY AS needed to digitize training and internal controls. With Trainly they went from physical binders and PDFs to a fully digital solution with complete oversight.",
    "landing.case.city.quote": "With Trainly we have structured and digitized our training. Employees are more confident, independent and deliver higher quality every day. We notice that employees feel well prepared, which makes them even better colleagues.",
    "landing.case.city.stat1": "Faster onboarding",
    "landing.case.city.stat2": "Trackable training",
    "landing.case.city.stat3": "Reduced costs",
    "landing.case.city.stat4": "Less administration",
    "landing.case.city.c1": "Training was in PDFs, emails and binders",
    "landing.case.city.c2": "No overview of who had completed what",
    "landing.case.city.c3": "Time-consuming onboarding of new employees",
    "landing.case.city.c4": "Difficult to update materials quickly",
    "landing.case.city.s1": "AI-powered training platform with automatic tracking",
    "landing.case.city.s2": "Digitization of all training materials",
    "landing.case.city.s3": "Automatic reminders and follow-up",
    "landing.case.city.s4": "Dashboards with real-time overview for managers",
    "landing.case.city.r1": "80–90% faster onboarding of new employees",
    "landing.case.city.r2": "Full documentation for audits and inspections",
    "landing.case.city.r3": "Less administration for management",
    "landing.case.city.r4": "Safer employees with better training",
    "landing.case.isdalen.tag": "Efficient driver training",
    "landing.case.isdalen.title": "From time-consuming follow-up to seamless digital training",
    "landing.case.isdalen.desc": "Isdalen Green Transport needed a solution to standardize driver training across departments. With Trainly they gained full control and saved significant time.",
    "landing.case.isdalen.quote": "Trainly has given us full insight and control over training. All courses were created quickly and easily using the built-in AI feature, and we now have documentation and status available at all times. It saves us time and gives us confidence in operations.",
    "landing.case.isdalen.stat1": "Faster onboarding",
    "landing.case.isdalen.stat2": "Trackable training",
    "landing.case.isdalen.stat3": "Less administration",
    "landing.case.isdalen.stat4": "Reduced costs",
    "landing.case.isdalen.c1": "Inconsistent training depending on department and instructor",
    "landing.case.isdalen.c2": "Manual driver follow-up took too much time",
    "landing.case.isdalen.c3": "Difficult to document completed training",
    "landing.case.isdalen.c4": "New drivers took too long to become operational",
    "landing.case.isdalen.s1": "Standardized digital training for all drivers",
    "landing.case.isdalen.s2": "Automatic tracking of progress and completion",
    "landing.case.isdalen.s3": "Courses available on mobile — perfect for drivers",
    "landing.case.isdalen.s4": "Certifications with automatic expiry alerts",
    "landing.case.isdalen.r1": "70% faster onboarding of new drivers",
    "landing.case.isdalen.r2": "Full traceability for audits and inspections",
    "landing.case.isdalen.r3": "40% less time spent on administration",
    "landing.case.isdalen.r4": "Higher competency levels across departments",
    "landing.case.ctaTitle": "Want to achieve similar results?",
    "landing.case.ctaSubtitle": "See how Trainly can streamline training in your business — no commitment.",
    "landing.case.ctaButton": "Book a demo",
    "landing.case.ctaPricing": "See pricing",
    "landing.guidePage.badge": "Learn about training",
    "landing.guidePage.title": "Guide to",
    "landing.guidePage.titleHighlight": "better training",
    "landing.guidePage.subtitle": "Practical articles on how to train employees faster, reduce turnover and ensure compliance — for all industries.",
    "landing.guidePage.ctaTitle": "Want to make training easier?",
    "landing.guidePage.ctaSubtitle": "Trainly makes it easy to create courses, train employees and track certifications — all in one system.",
    "landing.guidePage.ctaButton": "Book a free demo",
    "landing.guidePage.backToGuides": "Back to guides",
    "landing.guidePage.readArticle": "Read article",
    "landing.guidePage.improveCta": "Ready to improve your training?",
    "landing.guidePage.improveCtaSub": "Start with Trainly today and see results in days, not months.",
    "landing.guidePage.bookDemo": "Book a demo",

    // Manager widgets
    "widget.missingCourses": "Missing courses",
    "widget.drivers": "users",
    "widget.overdueDeadlines": "Overdue deadlines",
    "widget.daysOverdue": "d overdue",
    "widget.competencyCerts": "Competencies & certificates",
    "widget.expired": "Expired",
    "widget.expiringSoon": "Expiring soon",
    "widget.onboardingStatus": "Onboarding status",
    "widget.missing": "missing",
    "widget.notStarted": "Not started",
    "widget.inProgress": "In progress",
    "widget.courseStatus": "Course status",
    "widget.incomplete": "incomplete",
    "widget.allCompleted": "Everyone has completed their courses!",
    "widget.coursesCompleted": "courses completed",
    "widget.more": "more",

    // Landing social proof
    "landing.hero.socialProof": "We work with companies that are in control!",

    // Hero floating cards
    "landing.hero.floatSetup": "We set up for you",
    "landing.hero.floatSetupDesc": "Let us create courses and quizzes with AI, or upload your own material.",
    "landing.hero.floatInsight": "Insights",
    "landing.hero.floatInsightDesc": "Real-time overview per employee across all departments.",
    "landing.hero.floatOnboarding": "Faster onboarding",
    "landing.hero.floatOnboardingDesc": "Employees productive from day one.",
  },
} as const;

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem("app-language");
    return (stored === "en" || stored === "no") ? stored : "no";
  });

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("app-language", lang);
  };

  // Keep <html lang> in sync with the active language for correct indexing.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);


  const t = (key: TranslationKey, params?: Record<string, string | number>): string => {
    let text: string = translations[language][key] || String(key);
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        text = text.replace(`{${k}}`, String(v));
      });
    }
    return text;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
}
